import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Checkbox,
  ListItemText,
  OutlinedInput,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Star,
  StarBorder,
  TrendingUp,
  TrendingDown,
  ArrowBack,
  Refresh as RefreshIcon,
  HelpOutline as HelpOutlineIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import * as Plotly from 'plotly.js-dist-min';
import { toast } from 'react-toastify';
import { stockService, Stock, StockPrice, TechnicalAnalysis, EnhancedChartData, StockAdjustment } from '../../services/stockService';
import { portfolioService, StockTransactionsAcrossPortfoliosResponse, StockTransaction } from '../../services/portfolioService';
import StockTransactionHistory from './StockTransactionHistory';

// API Price data structure (what backend actually returns)
interface ApiPriceData {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface StockDetailsProps {
  stock: Stock;
  onBack: () => void;
  isInWatchlist: boolean;
  onToggleWatchlist: (stock: Stock) => void;
  initialTab?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Technical indicators available for display
const TECHNICAL_INDICATORS = [
  'Close Price',
  'Ohlc',
  'Support/Resistance',  // TradingView-style S/R levels
  'InvertedHammer',
  'ShootingStar',
  'HangingMan',
  'Hammer',
  'BullishEngulfing',
  'BearishEngulfing',
  'Jerk_s',
  'Jerk_e',
  'Accel_s',
  'Accel_e',
  'Volume',
  'ATR',
  'RSI',
  'Average',
  'SMA10',
  'SMA50',
  'SMA100',
  'SMA200',
  'EMA10',
  'EMA50',
  'EMA100',
  'EMA200',
  'Golden Crossovers',
  'Death Crossovers',
  'Golden Crossovers (S)',
  'Death Crossovers (S)',
  'Golden Crossovers (E)',
  'Death Crossovers (E)',
  'Peak_10',
  'Trough_10',
  'Peak_15',
  'Trough_15',
  'Peak_20',
  'Trough_20',
  'PointPos',
  'BreakoutPos'
];

// Indicators that should be plotted on separate subplots (not overlayed on price chart)
const SEPARATE_PLOT_INDICATORS = [
  'Jerk_s',
  'Jerk_e',
  'Accel_s',
  'Accel_e',
  'Volume',
  'ATR',
  'RSI'
];

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`stock-tabpanel-${index}`}
      aria-labelledby={`stock-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const StockDetails: React.FC<StockDetailsProps> = ({
  stock,
  onBack,
  isInWatchlist,
  onToggleWatchlist,
  initialTab = 0
}) => {
  const [stockPrices, setStockPrices] = useState<ApiPriceData[]>([]);
  const [enhancedChartData, setEnhancedChartData] = useState<EnhancedChartData | null>(null);
  const [latestPrice, setLatestPrice] = useState<StockPrice | null>(null);
  const [technicalAnalysis, setTechnicalAnalysis] = useState<TechnicalAnalysis | null>(null);
  const [fundamentalData, setFundamentalData] = useState<any>(null); // Add fundamentals state
  const [loading, setLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [tabValue, setTabValue] = useState(initialTab);
  const [timeframe, setTimeframe] = useState<number>(90); // Reduced from 365 to 90 days for faster loading
  const [selectedTechnicals, setSelectedTechnicals] = useState<string[]>(() => {
    // Load from localStorage or use defaults
    const saved = localStorage.getItem(`selectedTechnicals_${stock.symbol}`);
    const defaults = ['Close Price', 'EMA50', 'EMA200', 'Golden Crossovers (E)', 'Death Crossovers (E)', 'Volume'];
    const result = saved ? JSON.parse(saved) : defaults;
    console.log('📊 Loading selected technicals:', result);
    return result;
  }); // Default to match typical configuration
  const [chartType, setChartType] = useState<'line' | 'candlestick'>('line'); // Default to line chart
  const [isRenderingChart, setIsRenderingChart] = useState(false); // Flag to prevent duplicate renders
  const lastRenderDataRef = useRef<string>(''); // Track last rendered data to prevent unnecessary re-renders
  // Load chart groups from localStorage or use defaults
  const getDefaultChartGroups = () => ({
    'main': ['Close Price', 'Support/Resistance', 'SMA10', 'SMA50', 'SMA100', 'SMA200', 'EMA10', 'EMA50', 'EMA100', 'EMA200', 'Average', 'Golden Crossovers', 'Death Crossovers', 'Golden Crossovers (S)', 'Death Crossovers (S)', 'Golden Crossovers (E)', 'Death Crossovers (E)'],
    'volume': ['Volume'],
    'momentum': ['RSI', 'ATR'],
    'patterns': ['BullishEngulfing', 'BearishEngulfing', 'Hammer', 'InvertedHammer', 'ShootingStar', 'HangingMan'],
    'acceleration': ['Jerk_s', 'Jerk_e', 'Accel_s', 'Accel_e'],
    'peaks': ['Peak_10', 'Trough_10', 'Peak_15', 'Trough_15', 'Peak_20', 'Trough_20']
  });

  const [chartGroups, setChartGroups] = useState<{ [key: string]: string[] }>(() => {
    const saved = localStorage.getItem(`chartGroups_${stock.symbol}`);
    return saved ? JSON.parse(saved) : getDefaultChartGroups();
  });
  // Load active groups from localStorage or use defaults
  const getDefaultActiveGroups = () => ({
    'main': true,
    'volume': true,
    'momentum': false,
    'patterns': false,
    'acceleration': false,
    'peaks': false
  });

  const [activeGroups, setActiveGroups] = useState<{ [key: string]: boolean }>(() => {
    const saved = localStorage.getItem(`activeGroups_${stock.symbol}`);
    return saved ? JSON.parse(saved) : getDefaultActiveGroups();
  });
  const [showChartConfig, setShowChartConfig] = useState(false);
  const [draggedIndicator, setDraggedIndicator] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState<string>('');
  const [renamingGroup, setRenamingGroup] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState<string>('');
  
  // Saved chart views state
  const [savedViews, setSavedViews] = useState<{ [name: string]: { chartGroups: { [key: string]: string[] }, activeGroups: { [key: string]: boolean } } }>(() => {
    const saved = localStorage.getItem('savedChartViews');
    return saved ? JSON.parse(saved) : {};
  });
  const [saveViewName, setSaveViewName] = useState<string>('');
  const [showSaveViewDialog, setShowSaveViewDialog] = useState(false);

  // Transaction markers state for chart
  const [stockTransactions, setStockTransactions] = useState<StockTransaction[]>([]);
  const [showTransactionMarkers, setShowTransactionMarkers] = useState(true);

  // Stock Adjustment state
  const [adjustments, setAdjustments] = useState<StockAdjustment[]>([]);
  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
  const [adjustmentDate, setAdjustmentDate] = useState('');
  const [adjustmentType, setAdjustmentType] = useState<'stock_split' | 'bonus_shares'>('stock_split');
  const [adjustmentRatio, setAdjustmentRatio] = useState('');
  
  // Event impact analysis state
  const [eventHistory, setEventHistory] = useState<any[]>([]);
  const [eventSummary, setEventSummary] = useState<any>(null);
  const [showEventHistory, setShowEventHistory] = useState(false);
  const [eventHistoryLoading, setEventHistoryLoading] = useState(false);
  
  // Value and swing score state
  const [valueScore, setValueScore] = useState<any>(null);
  const [swingSignals, setSwingSignals] = useState<any[]>([]);
  const [adjustmentNotes, setAdjustmentNotes] = useState('');
  const [applyToFile, setApplyToFile] = useState(false); // Dry-run by default
  const [savingAdjustment, setSavingAdjustment] = useState(false);
  const [editingAdjustmentId, setEditingAdjustmentId] = useState<string | null>(null);

  const chartRef = useRef<HTMLDivElement>(null);

  // Save chart groups to localStorage when they change
  useEffect(() => {
    localStorage.setItem(`chartGroups_${stock.symbol}`, JSON.stringify(chartGroups));
  }, [chartGroups, stock.symbol]);

  // Save active groups to localStorage when they change
  useEffect(() => {
    localStorage.setItem(`activeGroups_${stock.symbol}`, JSON.stringify(activeGroups));
  }, [activeGroups, stock.symbol]);

  // Save selected technicals to localStorage when they change
  useEffect(() => {
    localStorage.setItem(`selectedTechnicals_${stock.symbol}`, JSON.stringify(selectedTechnicals));
  }, [selectedTechnicals, stock.symbol]);

  // Save saved views to localStorage when they change
  useEffect(() => {
    localStorage.setItem('savedChartViews', JSON.stringify(savedViews));
  }, [savedViews]);

  // Functions for saving and loading chart views
  const saveCurrentView = (viewName: string) => {
    if (!viewName.trim()) {
      toast.error('Please enter a view name');
      return;
    }
    
    const newViews = {
      ...savedViews,
      [viewName]: {
        chartGroups: { ...chartGroups },
        activeGroups: { ...activeGroups }
      }
    };
    setSavedViews(newViews);
    setSaveViewName('');
    setShowSaveViewDialog(false);
    toast.success(`View "${viewName}" saved successfully`);
  };

  const loadSavedView = (viewName: string) => {
    const view = savedViews[viewName];
    if (view) {
      setChartGroups(view.chartGroups);
      setActiveGroups(view.activeGroups);
      
      // Update selected technicals based on active groups
      const newSelectedTechnicals: string[] = [];
      Object.entries(view.activeGroups).forEach(([groupName, isActive]) => {
        if (isActive && view.chartGroups[groupName]) {
          newSelectedTechnicals.push(...view.chartGroups[groupName]);
        }
      });
      setSelectedTechnicals(Array.from(new Set(newSelectedTechnicals)));
      
      toast.success(`View "${viewName}" loaded`);
    }
  };

  const deleteSavedView = (viewName: string) => {
    const newViews = { ...savedViews };
    delete newViews[viewName];
    setSavedViews(newViews);
    toast.info(`View "${viewName}" deleted`);
  };

  // Auto-activate groups when their indicators are selected (but don't auto-deactivate)
  // This allows users to manually control group visibility while still auto-showing groups with selected indicators
  useEffect(() => {
    const newActiveGroups = { ...activeGroups };
    let changed = false;

    Object.entries(chartGroups).forEach(([groupName, indicators]) => {
      const hasSelectedIndicators = indicators.some(indicator => selectedTechnicals.includes(indicator));
      // Only auto-activate, never auto-deactivate (let user control deactivation manually)
      if (hasSelectedIndicators && !newActiveGroups[groupName]) {
        newActiveGroups[groupName] = true;
        changed = true;
        console.log(`📊 Auto-activating group "${groupName}" because it has selected indicators`);
      }
      // Removed auto-deactivation to preserve user's manual group settings
    });

    if (changed) {
      setActiveGroups(newActiveGroups);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTechnicals, chartGroups]); // Removed activeGroups from deps to prevent infinite loop

  // Enhanced cache to avoid re-fetching data across tabs
  const [dataCache, setDataCache] = useState<{ [key: string]: any }>({});
  const [technicalAnalysisLoaded, setTechnicalAnalysisLoaded] = useState(false);
  const [chartDataLoaded, setChartDataLoaded] = useState(false);
  const [fundamentalDataLoaded, setFundamentalDataLoaded] = useState(false);

  // Swing Trade Inputs state
  const [peakPct, setPeakPct] = useState<string>('');
  const [troughPct, setTroughPct] = useState<string>('');
  const [swingAnalysisLoading, setSwingAnalysisLoading] = useState(false);
  const [customSwingData, setCustomSwingData] = useState<any>(null);

  // Instant scraping state
  const [scrapingLoading, setScrapingLoading] = useState(false);
  const [scrapingResult, setScrapingResult] = useState<any>(null);

  // Support/Resistance settings
  const [srLevelCount, setSrLevelCount] = useState<number>(() => {
    const saved = localStorage.getItem(`srLevelCount_${stock.symbol}`);
    return saved ? parseInt(saved, 10) : 3;
  });
  const [showSRHelpModal, setShowSRHelpModal] = useState(false);

  // Save S/R level count to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(`srLevelCount_${stock.symbol}`, String(srLevelCount));
  }, [srLevelCount, stock.symbol]);

  // Removed unused periodOptions

  // Removed unused technicalIndicators array

  useEffect(() => {
    loadStockData();
    // Also load fundamental data for header information
    loadFundamentalData();
    // Load event history and investment profile
    loadEventHistory();
    loadInvestmentProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock.symbol, timeframe]);

  useEffect(() => {
    // Load fundamental data when fundamentals tab is selected (if not already loaded)
    if (tabValue === 2 && !fundamentalDataLoaded) {
      loadFundamentalData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, stock.symbol, fundamentalDataLoaded]);

  useEffect(() => {
    // Only load technical analysis when tab 1 (Technical Analysis) is active
    if (tabValue === 1 && !technicalAnalysisLoaded) {
      loadTechnicalAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock.symbol, tabValue, technicalAnalysisLoaded]);

  // Render chart when enhanced chart data changes
  useEffect(() => {
    if (chartRef.current && (stockPrices.length > 0 || enhancedChartData)) {
      console.log('🔄 Chart useEffect triggered, scheduling createChart()');
      const timeoutId = setTimeout(() => {
        createChart();
      }, 150);
      
      return () => {
        clearTimeout(timeoutId);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockPrices, enhancedChartData, timeframe, selectedTechnicals, chartType, customSwingData, srLevelCount, stockTransactions, showTransactionMarkers]);

  // Load stock adjustments when component mounts
  useEffect(() => {
    loadAdjustments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock.symbol]);

  // Load transactions for chart markers
  useEffect(() => {
    const loadTransactions = async () => {
      console.log(`📊 Loading transactions for ${stock.symbol}...`);
      try {
        const response = await portfolioService.getStockTransactionsAcrossPortfolios(stock.symbol);
        console.log(`📊 API response:`, response);
        // Flatten all transactions from all portfolios
        const allTransactions: StockTransaction[] = [];
        if (response.portfolios) {
          response.portfolios.forEach(portfolio => {
            console.log(`📊 Portfolio ${portfolio.portfolio_name}: ${portfolio.transactions?.length || 0} transactions`);
            if (portfolio.transactions) {
              allTransactions.push(...portfolio.transactions);
            }
          });
        }
        // Sort by date
        allTransactions.sort((a, b) => new Date(a.transaction_date).getTime() - new Date(b.transaction_date).getTime());
        setStockTransactions(allTransactions);
        console.log(`📊 Loaded ${allTransactions.length} transactions for chart markers`);
        if (allTransactions.length > 0) {
          console.log(`📊 First transaction:`, allTransactions[0]);
        }
      } catch (error: any) {
        console.error('📊 Error loading transactions:', error?.message || error);
        setStockTransactions([]);
      }
    };
    loadTransactions();
  }, [stock.symbol]);

  useEffect(() => {
    // Only load enhanced chart data when tab 0 (Chart) is active
    // Note: Added selectedTechnicals back to dependencies since we now use it to calculate effective timeframe
    console.log('🔄 useEffect triggered:', { tabValue, stockSymbol: stock.symbol, timeframe, selectedTechnicals });
    if (tabValue === 0) {
      console.log('📊 Tab is 0, loading enhanced chart data...');
      loadEnhancedChartData();
    } else {
      console.log('📊 Tab is not 0, skipping enhanced data load');
    }

    // Clear custom swing data when timeframe changes to prevent mismatch
    if (customSwingData) {
      console.log('🔄 Clearing custom swing data due to timeframe change');
      setCustomSwingData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, stock.symbol, tabValue, customSwingData, selectedTechnicals]);

  const loadEnhancedChartData = async () => {
    try {
      // The backend now handles extra lookback for SMA calculations
      // We just request the user's selected timeframe
      const effectiveTimeframe = timeframe;
      console.log('📊 Loading chart data:', {
        userTimeframe: timeframe,
        selectedTechnicals,
        effectiveTimeframe
      });

      const cacheKey = `enhanced_${stock.symbol}_${effectiveTimeframe}_all_indicators`;

      // Check cache first
      if (dataCache[cacheKey]) {
        console.log('📊 Using cached enhanced chart data for:', stock.symbol);
        setEnhancedChartData(dataCache[cacheKey]);
        return;
      }

      //console.log('📊 Loading chart data from database for:', stock.symbol, 'effective timeframe:', effectiveTimeframe, 'days, requesting all indicators');

      // Try enhanced method with all indicators first
      let data: any;
      try {
        console.log('🔄 Calling enhanced chart data endpoint with all indicators, effective timeframe:', effectiveTimeframe);
        data = await stockService.getEnhancedChartDataWithAllIndicators(stock.symbol, effectiveTimeframe, []);
        console.log('✅ Loaded enhanced chart data with all indicators successfully');
        console.log('📊 Enhanced data structure:', {
          dataSource: data.data_source,
          indicatorCount: Object.keys(data.indicators || {}).length,
          patternCount: Object.keys(data.patterns || {}).length,
          availableIndicators: data.available_indicators || [],
          sampleIndicators: Object.keys(data.indicators || {}).slice(0, 5)
        });
        console.log('📊 Enhanced data:', data);
      } catch (enhancedError) {
        console.warn('⚠️ Enhanced method failed, trying database-only method:', enhancedError);
        // Fallback to database-only method
        try {
          data = await stockService.getChartDataDbOnly(stock.symbol, effectiveTimeframe, selectedTechnicals);
          console.log('✅ Loaded chart data from database successfully');
        } catch (dbError) {
          console.warn('⚠️ Database-only method failed, trying original enhanced method:', dbError);
          // Final fallback to original enhanced method
          data = await stockService.getEnhancedChartData(stock.symbol, effectiveTimeframe, selectedTechnicals);
          console.log('✅ Loaded chart data using original enhanced method');
        }
      }

      // Cache the data
      setDataCache(prev => ({ ...prev, [cacheKey]: data }));
      setEnhancedChartData(data);

      console.log('✅ Loaded chart data:', {
        symbol: data.symbol,
        period: data.period,
        dataPoints: data.data?.length || 0,
        indicators: Object.keys(data.indicators || {}),
        supportResistance: !!data.support_resistance,
        patterns: Object.keys(data.patterns || {}),
        dataSource: (data as any).data_source || 'enhanced'
      });
    } catch (error: any) {
      console.error('❌ Failed to load chart data:', error);
      // Fallback to basic chart data
      setEnhancedChartData(null);
    }
  };

  const createChart = () => {
    if (!chartRef.current) return;

    // Defensive check for enhanced chart data
    const hasEnhancedData = enhancedChartData &&
      enhancedChartData.data &&
      Array.isArray(enhancedChartData.data) &&
      enhancedChartData.data.length > 0;

    // Use enhanced data if available, otherwise use basic price data
    const useEnhancedData = hasEnhancedData;
    let priceData = useEnhancedData ? enhancedChartData!.data : stockPrices;

    // Create a data signature to check if data has actually changed
    const dataSignature = JSON.stringify({
      priceDataLength: useEnhancedData ? enhancedChartData?.data?.length : stockPrices.length,
      timeframe,
      selectedTechnicals: selectedTechnicals.sort(),
      chartType,
      hasCustomSwing: !!customSwingData
    });

    // Skip render if data hasn't changed
    /*if (lastRenderDataRef.current === dataSignature) {
      console.log('📊 Data unchanged, skipping render');
      return;
    }*/

    console.log('🎯 Starting chart creation');
    setIsRenderingChart(true);
    lastRenderDataRef.current = dataSignature;

    // Filter enhanced data to match user's selected timeframe
    let filteredIndicators = enhancedChartData?.indicators || {};
    if (useEnhancedData && priceData.length > timeframe) {
      console.log('📊 Filtering enhanced data from', priceData.length, 'to', timeframe, 'data points');
      priceData = priceData.slice(-timeframe); // Take the most recent timeframe days
      
      // Also filter indicators to match the same timeframe
      if (enhancedChartData?.indicators) {
        filteredIndicators = {};
        Object.entries(enhancedChartData.indicators).forEach(([key, values]) => {
          if (Array.isArray(values) && values.length > timeframe) {
            filteredIndicators![key] = values.slice(-timeframe);
          } else {
            filteredIndicators![key] = values;
          }
        });
        console.log('📊 Filtered indicators to match timeframe');
      }
    }

    console.log('📊 Chart data source decision:', {
      useEnhancedData,
      enhancedDataAvailable: !!enhancedChartData,
      enhancedDataLength: enhancedChartData?.data?.length || 0,
      basicDataLength: stockPrices.length,
      finalDataLength: priceData.length,
      userTimeframe: timeframe,
      filtered: useEnhancedData && (enhancedChartData?.data?.length || 0) > timeframe
    });

    if (!priceData || priceData.length === 0) {
      console.warn('📊 No price data available for chart creation');
      return;
    }

    console.log('📊 Creating chart with', priceData.length, 'data points, enhanced:', useEnhancedData);
    const startTime = performance.now();

    // Handle both uppercase and lowercase property names
    const dates = priceData.map((price: any) => price.Date || price.date);
    const opens = priceData.map((price: any) => price.Open || price.open);
    const highs = priceData.map((price: any) => price.High || price.high);
    const lows = priceData.map((price: any) => price.Low || price.low);
    const closes = priceData.map((price: any) => price.Close || price.close);
    const volumes = priceData.map((price: any) => price.Volume || price.volume);

    // Debug: Check if we got valid data
    console.log('📊 Data extraction check:', {
      totalRecords: priceData.length,
      firstRecord: priceData[0],
      datesExtracted: dates.slice(0, 3),
      closesExtracted: closes.slice(0, 3),
      hasUndefined: dates.includes(undefined) || closes.includes(undefined)
    });

    // Validate that we have valid data arrays
    if (!dates || dates.length === 0 || !closes || closes.length === 0) {
      console.warn('📊 Invalid price data arrays for chart creation');
      return;
    }

    // Additional validation for large datasets
    if (dates.length > 5000) {
      console.warn('📊 Large dataset detected:', dates.length, 'points. This may affect performance.');
    }

    // Validate data arrays
    if (dates.length === 0) {
      console.warn('⚠️ No date data available');
      setIsRenderingChart(false);
      return;
    }

    // Calculate how many subplots we need based on chart groups that have selected indicators
    // A group is considered active if it has selected indicators, regardless of the activeGroups setting
    const activeGroupEntries = Object.entries(chartGroups).filter(([groupName, indicators]) =>
      indicators.some(indicator => selectedTechnicals.includes(indicator))
    );

    // Active groups with selected indicators

    // Each active group gets its own subplot
    const numSubplots = activeGroupEntries.length;

    // Helper function to get subplot index for an indicator
    const getSubplotIndex = (indicator: string): number => {
      const groupIndex = activeGroupEntries.findIndex(([groupName, indicators]) =>
        indicators.includes(indicator)
      );
      return groupIndex >= 0 ? groupIndex + 1 : 1; // 1-based indexing for y-axis
    };

    // Helper function to get yaxis name for an indicator
    const getYAxisName = (indicator: string): string => {
      // If no active groups, everything goes to main y-axis
      if (activeGroupEntries.length === 0) {
        return 'y';
      }

      const subplotIndex = getSubplotIndex(indicator);
      const yAxisName = subplotIndex === 1 ? 'y' : `y${subplotIndex}`;
      return yAxisName;
    };

    console.log('📊 Chart groups layout:', {
      activeGroups: activeGroupEntries.map(([name]) => name),
      numSubplots,
      chartGroups: activeGroupEntries,
      selectedTechnicals
    });

    // Debug: Log which indicators are in which groups
    console.log('📊 DEBUG: Chart group assignments:');
    activeGroupEntries.forEach(([groupName, indicators], index) => {
      const selectedInGroup = indicators.filter(ind => selectedTechnicals.includes(ind));
      console.log(`  Group ${index + 1} (${groupName}):`, selectedInGroup);
    });

    // Create main price trace (line chart by default)
    let mainTrace: any;

    // Check if backend indicates candlestick chart or if Ohlc is selected
    const shouldShowCandlestick = chartType === 'candlestick' ||
      selectedTechnicals.includes('Ohlc') ||
      (enhancedChartData && enhancedChartData.chart_type === 'candlestick');

    /*console.log('📊 Should show candlestick:', shouldShowCandlestick, {
      chartType,
      includesOhlc: selectedTechnicals.includes('Ohlc'),
      backendChartType: enhancedChartData?.chart_type
    });*/
    
    /*console.log('📊 Chart decision logic:', {
      condition1: chartType === 'line',
      condition2: !shouldShowCandlestick,
      willShowLine: chartType === 'line' && !shouldShowCandlestick,
      willShowCandlestick: !(chartType === 'line' && !shouldShowCandlestick)
    });*/

    if (chartType === 'line' && !shouldShowCandlestick) {
      // Default line chart showing close price
      mainTrace = {
        x: dates,
        y: closes,
        type: 'scatter',
        mode: 'lines',
        name: 'Close Price',
        line: { color: '#1976d2', width: 2 },
        yaxis: getYAxisName('Close Price')
      };
    } else {
      // OHLC candlestick chart when selected or indicated by backend
      mainTrace = {
        x: dates,
        open: opens,
        high: highs,
        low: lows,
        close: closes,
        type: 'candlestick',
        name: 'OHLC',
        increasing: { line: { color: '#00b894' } },
        decreasing: { line: { color: '#e17055' } },
        yaxis: getYAxisName('Ohlc')
      };
    }

    const traces = [mainTrace];
    const shapes: any[] = []; // For background regions like crossover periods

    // Add custom swing points from Swing Trade Inputs - plot on main price chart
    if (customSwingData && customSwingData.swing_points) {
      const peakKey = `Peak_${customSwingData.peak_pct}`;
      const troughKey = `Trough_${customSwingData.trough_pct}`;
      
      // Use the main chart y-axis for custom swing markers
      const mainYAxis = getYAxisName('Close Price');
      console.log('📊 Adding custom swing markers to main chart, yaxis:', mainYAxis);

      if (customSwingData.swing_points[peakKey]) {
        const customPeaks = customSwingData.swing_points[peakKey];
        const peakDates = customPeaks.map((item: any) => item.date);
        const peakValues = customPeaks.map((item: any) => item.value * 1.02); // Slightly above for visibility
        
        console.log(`📊 Adding ${customPeaks.length} custom peak markers at ${customSwingData.peak_pct}%`);

        traces.push({
          x: peakDates,
          y: peakValues,
          type: 'scatter',
          mode: 'markers+text',
          name: `Custom Peak ${customSwingData.peak_pct}%`,
          marker: {
            symbol: 'triangle-down',
            size: 14,
            color: 'rgba(220, 53, 69, 0.95)',
            line: { color: '#8B0000', width: 2 }
          },
          text: peakDates.map(() => '▼'),
          textposition: 'top center',
          textfont: { size: 10, color: 'rgba(220, 53, 69, 0.95)' },
          yaxis: mainYAxis,
          hovertemplate: `<b>Custom Peak ${customSwingData.peak_pct}%</b><br>Date: %{x}<br>Price: ₹%{customdata:.2f}<extra></extra>`,
          customdata: customPeaks.map((item: any) => item.value)
        });
      }

      if (customSwingData.swing_points[troughKey]) {
        const customTroughs = customSwingData.swing_points[troughKey];
        const troughDates = customTroughs.map((item: any) => item.date);
        const troughValues = customTroughs.map((item: any) => item.value * 0.98); // Slightly below for visibility
        
        console.log(`📊 Adding ${customTroughs.length} custom trough markers at ${customSwingData.trough_pct}%`);

        traces.push({
          x: troughDates,
          y: troughValues,
          type: 'scatter',
          mode: 'markers+text',
          name: `Custom Trough ${customSwingData.trough_pct}%`,
          marker: {
            symbol: 'triangle-up',
            size: 14,
            color: 'rgba(40, 167, 69, 0.95)',
            line: { color: '#006400', width: 2 }
          },
          text: troughDates.map(() => '▲'),
          textposition: 'bottom center',
          textfont: { size: 10, color: 'rgba(40, 167, 69, 0.95)' },
          yaxis: mainYAxis,
          hovertemplate: `<b>Custom Trough ${customSwingData.trough_pct}%</b><br>Date: %{x}<br>Price: ₹%{customdata:.2f}<extra></extra>`,
          customdata: customTroughs.map((item: any) => item.value)
        });
      }
    }

    // Add technical indicators from backend data or calculate locally
    if (useEnhancedData && enhancedChartData && enhancedChartData.indicators && typeof enhancedChartData.indicators === 'object') {
      const availableIndicators = Object.keys(filteredIndicators);
      // Check for missing indicators
      const missingIndicators = selectedTechnicals.filter(selected =>
        !availableIndicators.includes(selected) && selected !== 'Close Price'
      );
      if (missingIndicators.length > 0) {
        console.warn('⚠️ Selected indicators not found in backend data:', missingIndicators);
      }

      // Add moving averages and other line indicators
      Object.entries(filteredIndicators).forEach(([indicator, values]) => {
        if (!values || !Array.isArray(values) || values.length === 0) return;

        // Skip indicators not selected by user (unless no indicators are selected, then show none)
        if (selectedTechnicals.length > 0 && !selectedTechnicals.includes(indicator)) {
          return;
        }

        // Handle sparse indicators (Peak/Trough, Crossovers, Candlestick Patterns) differently
        const isSparseIndicator = indicator.includes('Peak_') || indicator.includes('Trough_') ||
          indicator.includes('Crossover') || indicator.includes('Golden') ||
          indicator.includes('Death') || indicator === 'Golden Crossovers' ||
          indicator === 'Death Crossovers' || indicator.includes('Engulfing') ||
          indicator.includes('Hammer') || indicator.includes('HangingMan') ||
          indicator.includes('ShootingStar') || indicator.includes('InvertedHammer');

        if (!isSparseIndicator && values.length !== dates.length) {
          console.warn(`⚠️ Indicator ${indicator} has ${values.length} values but dates has ${dates.length}. Skipping.`);
          return;
        }

        // Clean up NaN values
        const cleanValues = values.map((val: any) => {
          if (val === null || val === undefined || isNaN(val)) return null;
          return Number(val);
        });
        if (SEPARATE_PLOT_INDICATORS.includes(indicator)) {
          // Plot on separate subplot
          const subplotIndex = getSubplotIndex(indicator);

          if (indicator === 'Volume') {
            traces.push({
              x: dates,
              y: cleanValues,
              type: 'bar',
              name: 'Volume',
              marker: {
                color: 'rgba(25, 118, 210, 0.6)',
                line: { width: 0 }
              },
              yaxis: `y${subplotIndex}`,
              opacity: 0.8,
              hovertemplate: '<b>Volume</b><br>Date: %{x}<br>Volume: %{y:,.0f}<extra></extra>'
            });
          } else if (indicator === 'RSI') {
            // Filter out null/undefined RSI values and their corresponding dates
            const validRSIData = cleanValues.map((val, idx) => ({ value: val, date: dates[idx] }))
              .filter(item => item.value !== null && item.value !== undefined && !isNaN(item.value));

            if (validRSIData.length > 0) {
              const rsiDates = validRSIData.map(item => item.date);
              const rsiValues = validRSIData.map(item => item.value);

              console.log('📊 RSI data points:', rsiValues.length, 'valid values out of', cleanValues.length);
              console.log('📊 RSI sample values:', rsiValues.slice(0, 5), '...', rsiValues.slice(-5));

              traces.push({
                x: rsiDates,
                y: rsiValues,
                type: 'scatter',
                mode: 'lines',
                name: 'RSI',
                line: { color: '#8E44AD', width: 2 },
                yaxis: `y${subplotIndex}`,
                hovertemplate: '<b>RSI</b><br>Date: %{x}<br>RSI: %{y:.2f}<extra></extra>'
              });

              // Add RSI reference lines with the same x-axis as RSI data
              if (rsiDates.length > 0) {
                // 70 line (overbought)
                traces.push({
                  x: [rsiDates[0], rsiDates[rsiDates.length - 1]],
                  y: [70, 70],
                  type: 'scatter',
                  mode: 'lines',
                  name: 'Overbought (70)',
                  line: { color: 'rgba(231, 76, 60, 0.8)', dash: 'dash', width: 1 },
                  showlegend: false,
                  yaxis: `y${subplotIndex}`
                });

                // 30 line (oversold)
                traces.push({
                  x: [rsiDates[0], rsiDates[rsiDates.length - 1]],
                  y: [30, 30],
                  type: 'scatter',
                  mode: 'lines',
                  name: 'Oversold (30)',
                  line: { color: 'rgba(46, 204, 113, 0.8)', dash: 'dash', width: 1 },
                  showlegend: false,
                  yaxis: `y${subplotIndex}`
                });

                // 50 line (neutral)
                traces.push({
                  x: [rsiDates[0], rsiDates[rsiDates.length - 1]],
                  y: [50, 50],
                  type: 'scatter',
                  mode: 'lines',
                  name: 'Neutral (50)',
                  line: { color: 'rgba(128, 128, 128, 0.5)', dash: 'dot', width: 1 },
                  showlegend: false,
                  yaxis: `y${subplotIndex}`
                });
              }
            } else {
              console.warn('📊 No valid RSI data points found');
            }
          } else {
            // Style other separate indicators
            const separateColorMap: { [key: string]: string } = {
              'ATR': '#E74C3C',
              'Jerk_s': '#3498DB',
              'Jerk_e': '#9B59B6',
              'Accel_s': '#F39C12',
              'Accel_e': '#1ABC9C'
            };

            traces.push({
              x: dates,
              y: cleanValues,
              type: 'scatter',
              mode: 'lines',
              name: indicator,
              line: {
                color: separateColorMap[indicator] || '#34495E',
                width: 2
              },
              yaxis: `y${subplotIndex}`,
              hovertemplate: `<b>${indicator}</b><br>Date: %{x}<br>Value: %{y:.2f}<extra></extra>`
            });
          }
        } else if (isSparseIndicator) {
          // Handle sparse indicators as scatter markers
          if (indicator.includes('Peak_')) {
            // Peak markers - green triangles pointing up, positioned slightly above the price
            const peakDates = values.map((item: any) => item.date);
            const peakValues = values.map((item: any) => item.value * 1.015); // 1.5% above the actual peak

            traces.push({
              x: peakDates,
              y: peakValues,
              type: 'scatter',
              mode: 'markers',
              name: indicator,
              marker: {
                symbol: 'triangle-up',
                size: 10,
                color: 'rgba(0, 150, 0, 0.8)',
                line: { color: 'darkgreen', width: 2 }
              },
              yaxis: getYAxisName(indicator),
              hovertemplate: `<b>${indicator}</b><br>Date: %{x}<br>Actual Peak: ₹%{customdata:.2f}<extra></extra>`,
              customdata: values.map((item: any) => item.value) // Store actual values for hover
            });
          } else if (indicator.includes('Trough_')) {
            // Trough markers - red triangles pointing down, positioned slightly below the price
            const troughDates = values.map((item: any) => item.date);
            const troughValues = values.map((item: any) => item.value * 0.985); // 1.5% below the actual trough

            traces.push({
              x: troughDates,
              y: troughValues,
              type: 'scatter',
              mode: 'markers',
              name: indicator,
              marker: {
                symbol: 'triangle-down',
                size: 10,
                color: 'rgba(200, 0, 0, 0.8)',
                line: { color: 'darkred', width: 2 }
              },
              yaxis: getYAxisName(indicator),
              hovertemplate: `<b>${indicator}</b><br>Date: %{x}<br>Actual Trough: ₹%{customdata:.2f}<extra></extra>`,
              customdata: values.map((item: any) => item.value) // Store actual values for hover
            });
          } else if (indicator.includes('Golden') || indicator.includes('Crossover')) {
            // Store crossover events for background regions (handled separately below)
            // Don't add as traces here - they will be processed as background regions
            console.log(`📊 Storing ${indicator} events for background regions: ${values.length} events`);
          } else if (indicator.includes('Engulfing') || indicator.includes('Hammer') ||
            indicator.includes('HangingMan') || indicator.includes('ShootingStar') ||
            indicator.includes('InvertedHammer')) {
            // Candlestick patterns - different colors and symbols for each
            const patternDates = values.map((item: any) => item.date);
            const patternValues = values.map((item: any) => item.value);

            // Determine color and symbol based on pattern type
            let color = 'rgba(100, 100, 100, 0.8)';
            let symbol = 'circle';
            let name = indicator;

            if (indicator.includes('BullishEngulfing')) {
              color = 'rgba(0, 200, 0, 0.9)';
              symbol = 'triangle-up';
              name = 'Bullish Engulfing';
            } else if (indicator.includes('BearishEngulfing')) {
              color = 'rgba(200, 0, 0, 0.9)';
              symbol = 'triangle-down';
              name = 'Bearish Engulfing';
            } else if (indicator.includes('Hammer')) {
              color = 'rgba(0, 150, 0, 0.8)';
              symbol = 'diamond';
              name = 'Hammer';
            } else if (indicator.includes('HangingMan')) {
              color = 'rgba(200, 100, 0, 0.8)';
              symbol = 'diamond';
              name = 'Hanging Man';
            } else if (indicator.includes('ShootingStar')) {
              color = 'rgba(200, 0, 100, 0.8)';
              symbol = 'star';
              name = 'Shooting Star';
            } else if (indicator.includes('InvertedHammer')) {
              color = 'rgba(100, 0, 200, 0.8)';
              symbol = 'star';
              name = 'Inverted Hammer';
            }

            traces.push({
              x: patternDates,
              y: patternValues,
              type: 'scatter',
              mode: 'markers',
              name: name,
              marker: {
                symbol: symbol,
                size: 8,
                color: color,
                line: { color: color.replace('0.8', '1.0'), width: 1 }
              },
              yaxis: getYAxisName(indicator),
              hovertemplate: `<b>${name}</b><br>Date: %{x}<br>Price: ₹%{y:.2f}<extra></extra>`
            });
          }
        } else {
          // Plot on main chart with better color differentiation
          const colorMap: { [key: string]: string } = {
            'SMA10': '#FF6B6B',
            'SMA50': '#4ECDC4',
            'SMA100': '#45B7D1',
            'SMA200': '#96CEB4',
            'EMA10': '#FFEAA7',
            'EMA50': '#DDA0DD',
            'EMA100': '#98D8C8',
            'EMA200': '#F7DC6F',
            'Average': '#BB8FCE'
          };

          traces.push({
            x: dates,
            y: cleanValues,
            type: 'scatter',
            mode: 'lines',
            name: indicator,
            line: {
              width: indicator.includes('200') ? 2 : 1,
              color: colorMap[indicator] || '#666666'
            },
            yaxis: getYAxisName(indicator),
            opacity: 0.8
          });
        }
      });

      // Add patterns (peaks, troughs, candlestick patterns, crossovers)
      if (enhancedChartData.patterns && typeof enhancedChartData.patterns === 'object') {
        // Handle crossovers with colored areas - only show if selected
        const shouldShowGoldenCrossovers = selectedTechnicals.length === 0 ||
          selectedTechnicals.includes('Golden Crossovers (E)') ||
          selectedTechnicals.includes('Golden Crossovers (S)') ||
          selectedTechnicals.includes('Golden Crossovers');
        const shouldShowDeathCrossovers = selectedTechnicals.length === 0 ||
          selectedTechnicals.includes('Death Crossovers (E)') ||
          selectedTechnicals.includes('Death Crossovers (S)') ||
          selectedTechnicals.includes('Death Crossovers');

        const goldenCrossovers = shouldShowGoldenCrossovers ?
          (enhancedChartData.patterns['Golden Crossovers (E)'] || enhancedChartData.patterns['Golden Crossovers (S)'] || []) : [];
        const deathCrossovers = shouldShowDeathCrossovers ?
          (enhancedChartData.patterns['Death Crossovers (E)'] || enhancedChartData.patterns['Death Crossovers (S)'] || []) : [];
        
        // Process crossover data
        
        // Debug: Check for anomalous crossover dates and filter them out
        const dataStartDate = new Date(dates[0]);
        const dataEndDate = new Date(dates[dates.length - 1]);
        
        // Validate that we have valid dates
        let validGoldenCrossovers, validDeathCrossovers;
        
        if (isNaN(dataStartDate.getTime()) || isNaN(dataEndDate.getTime())) {
          // Using crossovers as-is due to invalid date range
          // Use original crossovers without filtering
          validGoldenCrossovers = goldenCrossovers;
          validDeathCrossovers = deathCrossovers;
        } else {
          // Filter out crossovers with invalid dates or dates outside data range
          validGoldenCrossovers = goldenCrossovers.filter(c => {
            const crossoverDate = new Date(c.date);
            return !isNaN(crossoverDate.getTime()) && 
                   crossoverDate >= dataStartDate && 
                   crossoverDate <= dataEndDate;
          });
          
          validDeathCrossovers = deathCrossovers.filter(c => {
            const crossoverDate = new Date(c.date);
            return !isNaN(crossoverDate.getTime()) && 
                   crossoverDate >= dataStartDate && 
                   crossoverDate <= dataEndDate;
          });
          
          const invalidCrossovers = [...goldenCrossovers, ...deathCrossovers].filter(c => {
            const crossoverDate = new Date(c.date);
            return isNaN(crossoverDate.getTime()) || 
                   crossoverDate < dataStartDate || 
                   crossoverDate > dataEndDate;
          });
          
          if (invalidCrossovers.length > 0) {
            console.warn('📊 Filtered out invalid crossover dates:', invalidCrossovers.map(c => c.date));
          }
          
          console.log('📊 Valid crossovers after filtering:', {
            golden: validGoldenCrossovers.length,
            death: validDeathCrossovers.length,
            dataRange: `${dataStartDate.toISOString().split('T')[0]} to ${dataEndDate.toISOString().split('T')[0]}`
          });
        }

        // Create colored areas between crossovers (use filtered valid crossovers)
        if (validGoldenCrossovers.length > 0 || validDeathCrossovers.length > 0) {
          // Combine and sort all valid crossovers by date
          const allCrossovers = [
            ...validGoldenCrossovers.map((p: any) => ({ ...p, type: 'golden' })),
            ...validDeathCrossovers.map((p: any) => ({ ...p, type: 'death' }))
          ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

          // Create vertical areas between crossovers
          for (let i = 0; i < allCrossovers.length; i++) {
            const currentCrossover = allCrossovers[i];
            const nextCrossover = allCrossovers[i + 1];

            // Determine end date for this area
            let endDate;
            if (nextCrossover) {
              endDate = nextCrossover.date;
            } else {
              // If this is the last crossover, extend to the end of the chart
              endDate = dates[dates.length - 1];
            }

            // Determine color based on crossover type
            const isGolden = currentCrossover.type === 'golden';
            const fillColor = isGolden ? 'rgba(255, 215, 0, 0.15)' : 'rgba(255, 0, 0, 0.15)'; // Golden yellow or red
            const lineColor = isGolden ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255, 0, 0, 0.3)';

            // Create background region for this crossover period
            const shapeConfig = {
              type: 'rect',
              xref: 'x',
              yref: 'paper', // Use paper coordinates for full height
              x0: currentCrossover.date,
              x1: endDate,
              y0: 0,
              y1: 1,
              fillcolor: fillColor,
              line: { width: 0 },
              layer: 'below' // Place behind the traces
            };
            shapes.push(shapeConfig);

            // Created crossover background region

            // Add a vertical line at the crossover point for clarity
            traces.push({
              x: [currentCrossover.date, currentCrossover.date],
              y: [Math.min(...closes) * 0.95, Math.max(...closes) * 1.05],
              type: 'scatter',
              mode: 'lines',
              name: isGolden ? `Golden Crossover (${currentCrossover.date})` : `Death Crossover (${currentCrossover.date})`,
              line: { color: lineColor, width: 2, dash: 'dash' },
              yaxis: getYAxisName('Golden Crossovers'),
              showlegend: false, // Don't clutter legend with crossover lines
              hovertemplate: `<b>${isGolden ? 'Golden' : 'Death'} Crossover</b><br>Date: %{x}<br>Price: ₹${currentCrossover.value?.toFixed(2) || 'N/A'}<extra></extra>`
            });
          }
        }

        // Handle other patterns (peaks, troughs, candlestick patterns)
        Object.entries(enhancedChartData.patterns).forEach(([patternName, points]) => {
          // Skip crossovers as they're handled above
          if (patternName.includes('Crossover')) return;

          if (points && Array.isArray(points) && points.length > 0) {
            const patternDates = points.map((p: any) => p.date);
            const patternPrices = points.map((p: any) => p.price);

            let markerConfig: any = { size: 8 };

            if (patternName.includes('Peak')) {
              markerConfig = { symbol: 'triangle-up', color: 'green', size: 8 };
            } else if (patternName.includes('Trough')) {
              markerConfig = { symbol: 'triangle-down', color: 'red', size: 8 };
            } else {
              markerConfig = { symbol: 'diamond', color: 'purple', size: 6 };
            }

            traces.push({
              x: patternDates,
              y: patternPrices,
              type: 'scatter',
              mode: 'markers',
              name: patternName,
              marker: markerConfig,
              yaxis: getYAxisName(patternName)
            });
          }
        });
      }

      // Add TradingView-style support and resistance levels
      const showSupportResistance = selectedTechnicals.includes('Support/Resistance') || selectedTechnicals.length === 0;
      
      if (showSupportResistance && enhancedChartData.support_resistance &&
        typeof enhancedChartData.support_resistance === 'object' &&
        dates && dates.length > 0) {
        
        const srData = enhancedChartData.support_resistance;
        const levels = srData.levels || [];
        const currentPrice = srData.current_price || closes[closes.length - 1];
        
        console.log('📊 Rendering Support/Resistance levels:', levels.length, 'total levels, showing', srLevelCount, 'each');
        
        // TradingView-style color scheme
        const resistanceColor = 'rgba(239, 83, 80, 0.9)';  // Red for resistance
        const supportColor = 'rgba(38, 166, 154, 0.9)';    // Green/Teal for support
        
        // Separate levels by type and limit by user preference
        const resistanceLevels = levels.filter((l: any) => l.type === 'resistance').slice(0, srLevelCount);
        const supportLevels = levels.filter((l: any) => l.type === 'support').slice(0, srLevelCount);
        
        // Add resistance levels (above current price) - add these first
        resistanceLevels.forEach((level: any, idx: number) => {
          const opacity = Math.max(0.5, 1 - (idx * 0.12));  // Fade out further levels
          const lineWidth = level.touch_count >= 3 ? 2 : 1.5;
          const dashStyle = level.significance >= 3 ? 'solid' : 'dash';
          
          // Main resistance line
          traces.push({
            x: [dates[0], dates[dates.length - 1]],
            y: [level.price, level.price],
            type: 'scatter',
            mode: 'lines',
            name: `R${idx + 1}: ₹${level.price.toFixed(0)} (${level.touch_count}x)`,
            line: { 
              color: resistanceColor.replace('0.9', String(opacity)), 
              dash: dashStyle, 
              width: lineWidth 
            },
            yaxis: getYAxisName('Close Price'),
            legendgroup: 'resistance',
            hovertemplate: `<b>Resistance Level ${idx + 1}</b><br>` +
              `Price: ₹${level.price.toFixed(2)}<br>` +
              `Distance: ${level.distance_pct > 0 ? '+' : ''}${level.distance_pct.toFixed(2)}%<br>` +
              `Touches: ${level.touch_count}<br>` +
              `Significance: ${level.significance.toFixed(1)}<extra></extra>`
          });
        });
        
        // Add support levels (below current price) - add these after resistance
        supportLevels.forEach((level: any, idx: number) => {
          const opacity = Math.max(0.5, 1 - (idx * 0.12));  // Fade out further levels
          const lineWidth = level.touch_count >= 3 ? 2 : 1.5;
          const dashStyle = level.significance >= 3 ? 'solid' : 'dash';
          
          // Main support line
          traces.push({
            x: [dates[0], dates[dates.length - 1]],
            y: [level.price, level.price],
            type: 'scatter',
            mode: 'lines',
            name: `S${idx + 1}: ₹${level.price.toFixed(0)} (${level.touch_count}x)`,
            line: { 
              color: supportColor.replace('0.9', String(opacity)), 
              dash: dashStyle, 
              width: lineWidth 
            },
            yaxis: getYAxisName('Close Price'),
            legendgroup: 'support',
            hovertemplate: `<b>Support Level ${idx + 1}</b><br>` +
              `Price: ₹${level.price.toFixed(2)}<br>` +
              `Distance: ${level.distance_pct.toFixed(2)}%<br>` +
              `Touches: ${level.touch_count}<br>` +
              `Significance: ${level.significance.toFixed(1)}<extra></extra>`
          });
        });
        
        // Add current price line for reference
        traces.push({
          x: [dates[0], dates[dates.length - 1]],
          y: [currentPrice, currentPrice],
          type: 'scatter',
          mode: 'lines',
          name: `Current: ₹${currentPrice.toFixed(2)}`,
          line: { 
            color: 'rgba(255, 193, 7, 0.8)',  // Amber/Yellow
            dash: 'dot', 
            width: 1.5 
          },
          yaxis: getYAxisName('Close Price'),
          showlegend: false
        });
        
        // Legacy fallback for simple support/resistance if no levels array
        if (levels.length === 0 && srData.support && srData.resistance) {
          traces.push({
            x: [dates[0], dates[dates.length - 1]],
            y: [srData.support, srData.support],
            type: 'scatter',
            mode: 'lines',
            name: `Support: ₹${srData.support.toFixed(2)}`,
            line: { color: supportColor, dash: 'solid', width: 2 },
            yaxis: getYAxisName('Close Price')
          });
          
          traces.push({
            x: [dates[0], dates[dates.length - 1]],
            y: [srData.resistance, srData.resistance],
            type: 'scatter',
            mode: 'lines',
            name: `Resistance: ₹${srData.resistance.toFixed(2)}`,
            line: { color: resistanceColor, dash: 'solid', width: 2 },
            yaxis: getYAxisName('Close Price')
          });
        }
      }
    } else {
      // Fallback: calculate basic indicators locally when enhanced data is not available
      console.log('📊 Using fallback calculations for technical indicators');

      // Calculate RSI locally if requested
      if (selectedTechnicals.includes('RSI') && closes.length > 14) {
        const rsiValues = calculateRSI(closes, 14);
        const validRSIData = rsiValues.map((val, idx) => ({ value: val, date: dates[idx] }))
          .filter(item => item.value !== null && item.value !== undefined && !isNaN(item.value));

        if (validRSIData.length > 0) {
          const rsiDates = validRSIData.map(item => item.date);
          const rsiValuesClean = validRSIData.map(item => item.value);
          const subplotIndex = getSubplotIndex('RSI');

          traces.push({
            x: rsiDates,
            y: rsiValuesClean,
            type: 'scatter',
            mode: 'lines',
            name: 'RSI (Local)',
            line: { color: '#8E44AD', width: 2 },
            yaxis: `y${subplotIndex}`,
            hovertemplate: '<b>RSI</b><br>Date: %{x}<br>RSI: %{y:.2f}<extra></extra>'
          });

          // Add RSI reference lines
          if (rsiDates.length > 0) {
            traces.push({
              x: [rsiDates[0], rsiDates[rsiDates.length - 1]],
              y: [70, 70],
              type: 'scatter',
              mode: 'lines',
              name: 'Overbought (70)',
              line: { color: 'rgba(231, 76, 60, 0.8)', dash: 'dash', width: 1 },
              showlegend: false,
              yaxis: `y${subplotIndex}`
            });

            traces.push({
              x: [rsiDates[0], rsiDates[rsiDates.length - 1]],
              y: [30, 30],
              type: 'scatter',
              mode: 'lines',
              name: 'Oversold (30)',
              line: { color: 'rgba(46, 204, 113, 0.8)', dash: 'dash', width: 1 },
              showlegend: false,
              yaxis: `y${subplotIndex}`
            });

            traces.push({
              x: [rsiDates[0], rsiDates[rsiDates.length - 1]],
              y: [50, 50],
              type: 'scatter',
              mode: 'lines',
              name: 'Neutral (50)',
              line: { color: 'rgba(128, 128, 128, 0.5)', dash: 'dot', width: 1 },
              showlegend: false,
              yaxis: `y${subplotIndex}`
            });
          }
        }
      }

      // Calculate Volume if requested (already available in basic data)
      if (selectedTechnicals.includes('Volume')) {
        traces.push({
          x: dates,
          y: volumes,
          type: 'bar',
          name: 'Volume',
          marker: {
            color: 'rgba(25, 118, 210, 0.6)',
            line: { width: 0 }
          },
          yaxis: getYAxisName('Volume'),
          opacity: 0.8,
          hovertemplate: '<b>Volume</b><br>Date: %{x}<br>Volume: %{y:,.0f}<extra></extra>'
        });
      }

      // Calculate simple moving averages if requested
      ['SMA10', 'SMA50', 'SMA100', 'SMA200'].forEach(smaType => {
        if (selectedTechnicals.includes(smaType)) {
          const period = parseInt(smaType.replace('SMA', ''));
          const smaValues = calculateSMA(closes, period);

          const colorMap: { [key: string]: string } = {
            'SMA10': '#FF6B6B',
            'SMA50': '#4ECDC4',
            'SMA100': '#45B7D1',
            'SMA200': '#96CEB4'
          };

          traces.push({
            x: dates,
            y: smaValues,
            type: 'scatter',
            mode: 'lines',
            name: `${smaType} (Local)`,
            line: {
              width: smaType === 'SMA200' ? 2 : 1,
              color: colorMap[smaType] || '#666666'
            },
            yaxis: getYAxisName(smaType),
            opacity: 0.8
          });
        }
      });

      // Calculate exponential moving averages if requested
      ['EMA10', 'EMA50', 'EMA100', 'EMA200'].forEach(emaType => {
        if (selectedTechnicals.includes(emaType)) {
          const period = parseInt(emaType.replace('EMA', ''));
          const emaValues = calculateEMA(closes, period);

          const colorMap: { [key: string]: string } = {
            'EMA10': '#FFEAA7',
            'EMA50': '#DDA0DD',
            'EMA100': '#98D8C8',
            'EMA200': '#F7DC6F'
          };

          traces.push({
            x: dates,
            y: emaValues,
            type: 'scatter',
            mode: 'lines',
            name: `${emaType} (Local)`,
            line: {
              width: emaType === 'EMA200' ? 2 : 1,
              color: colorMap[emaType] || '#666666'
            },
            yaxis: getYAxisName(emaType),
            opacity: 0.8
          });
        }
      });

      // Calculate crossovers locally if requested and not available from backend
      const shouldShowGoldenCrossovers = selectedTechnicals.some(tech => tech.includes('Golden Crossovers'));
      const shouldShowDeathCrossovers = selectedTechnicals.some(tech => tech.includes('Death Crossovers'));

      if (shouldShowGoldenCrossovers || shouldShowDeathCrossovers) {
        console.log('📊 Calculating crossovers locally (fallback mode)');

        // Calculate EMA50 and EMA200 for crossover detection
        const ema50 = calculateEMA(closes, 50);
        const ema200 = calculateEMA(closes, 200);

        const crossovers = calculateCrossovers(ema50, ema200, dates, closes);

        // Add golden crossover markers
        if (shouldShowGoldenCrossovers && crossovers.golden.length > 0) {
          console.log(`📊 Adding ${crossovers.golden.length} golden crossover markers`);
          crossovers.golden.forEach((crossover, index) => {
            traces.push({
              x: [crossover.date],
              y: [crossover.price],
              type: 'scatter',
              mode: 'markers',
              name: index === 0 ? 'Golden Crossovers (Local)' : '',
              marker: {
                color: '#00b894',
                size: 12,
                symbol: 'triangle-up',
                line: { color: '#00a085', width: 2 }
              },
              yaxis: getYAxisName('Golden Crossovers (E)'),
              showlegend: index === 0,
              hovertemplate: `<b>Golden Crossover</b><br>Date: %{x}<br>Price: ₹${crossover.price.toFixed(2)}<extra></extra>`
            });
          });
        }

        // Add death crossover markers
        if (shouldShowDeathCrossovers && crossovers.death.length > 0) {
          console.log(`📊 Adding ${crossovers.death.length} death crossover markers`);
          crossovers.death.forEach((crossover, index) => {
            traces.push({
              x: [crossover.date],
              y: [crossover.price],
              type: 'scatter',
              mode: 'markers',
              name: index === 0 ? 'Death Crossovers (Local)' : '',
              marker: {
                color: '#e17055',
                size: 12,
                symbol: 'triangle-down',
                line: { color: '#d63031', width: 2 }
              },
              yaxis: getYAxisName('Death Crossovers (E)'),
              showlegend: index === 0,
              hovertemplate: `<b>Death Crossover</b><br>Date: %{x}<br>Price: ₹${crossover.price.toFixed(2)}<extra></extra>`
            });
          });
        }
      }

      // Fallback: calculate basic support/resistance for basic chart (only if selected)
      if (selectedTechnicals.includes('Support_Resistance')) {
        const supportResistance = calculateSupportResistance(highs, lows, closes);
        if (supportResistance.support && supportResistance.resistance) {
          // Support line
          if (dates && dates.length > 0) {
            traces.push({
              x: [dates[0], dates[dates.length - 1]],
              y: [supportResistance.support, supportResistance.support],
              type: 'scatter',
              mode: 'lines',
              name: 'Support',
              line: { color: 'green', dash: 'solid', width: 2 },
              yaxis: getYAxisName('Support')
            });

            // Resistance line
            traces.push({
              x: [dates[0], dates[dates.length - 1]],
              y: [supportResistance.resistance, supportResistance.resistance],
              type: 'scatter',
              mode: 'lines',
              name: 'Resistance',
              line: { color: 'red', dash: 'solid', width: 2 },
              yaxis: getYAxisName('Resistance')
            });
          }
        }
      }
    }

    // Add transaction markers to the chart
    console.log(`📊 Transaction markers check: showTransactionMarkers=${showTransactionMarkers}, stockTransactions.length=${stockTransactions.length}`);
    if (showTransactionMarkers && stockTransactions.length > 0) {
      // Filter transactions within the chart date range
      const chartStartDate = dates.length > 0 ? new Date(dates[0]) : null;
      const chartEndDate = dates.length > 0 ? new Date(dates[dates.length - 1]) : null;
      
      console.log(`📊 Chart date range: ${chartStartDate?.toISOString()} to ${chartEndDate?.toISOString()}`);
      console.log(`📊 Sample transaction dates:`, stockTransactions.slice(0, 3).map(tx => tx.transaction_date));
      
      const visibleTransactions = stockTransactions.filter(tx => {
        const txDate = new Date(tx.transaction_date);
        return chartStartDate && chartEndDate && txDate >= chartStartDate && txDate <= chartEndDate;
      });
      
      console.log(`📊 Visible transactions after date filter: ${visibleTransactions.length}`);

      if (visibleTransactions.length > 0) {
        // Separate buy and sell transactions (API returns lowercase: 'buy', 'sell', 'open_position')
        const buyTransactions = visibleTransactions.filter(tx => {
          const type = tx.transaction_type?.toLowerCase();
          return type === 'buy' || type === 'open_position';
        });
        const sellTransactions = visibleTransactions.filter(tx => {
          const type = tx.transaction_type?.toLowerCase();
          return type === 'sell';
        });

        // Find the price at each transaction date from the chart data
        const getPriceAtDate = (txDate: string) => {
          const txDateStr = txDate.split('T')[0];
          const idx = dates.findIndex(d => d.startsWith(txDateStr));
          if (idx >= 0) return closes[idx];
          // If exact date not found, find closest
          const txTime = new Date(txDate).getTime();
          let closestIdx = 0;
          let closestDiff = Infinity;
          dates.forEach((d, i) => {
            const diff = Math.abs(new Date(d).getTime() - txTime);
            if (diff < closestDiff) {
              closestDiff = diff;
              closestIdx = i;
            }
          });
          return closes[closestIdx];
        };

        // Aggregate transactions by date
        interface AggregatedTx {
          date: string;
          totalQty: number;
          totalValue: number;
          avgPrice: number;
          count: number;
          details: string[];
        }

        const aggregateByDate = (transactions: typeof buyTransactions): AggregatedTx[] => {
          const byDate: { [date: string]: AggregatedTx } = {};
          transactions.forEach(tx => {
            const dateStr = tx.transaction_date.split('T')[0];
            if (!byDate[dateStr]) {
              byDate[dateStr] = { date: dateStr, totalQty: 0, totalValue: 0, avgPrice: 0, count: 0, details: [] };
            }
            byDate[dateStr].totalQty += tx.quantity;
            byDate[dateStr].totalValue += tx.total_value;
            byDate[dateStr].count += 1;
            byDate[dateStr].details.push(`${tx.quantity} @ ₹${tx.price.toFixed(2)} = ₹${tx.total_value.toFixed(2)}`);
          });
          // Calculate average price
          Object.values(byDate).forEach(agg => {
            agg.avgPrice = agg.totalQty > 0 ? agg.totalValue / agg.totalQty : 0;
          });
          return Object.values(byDate);
        };

        // Add buy markers (text only, no arrows)
        if (buyTransactions.length > 0) {
          const aggregatedBuys = aggregateByDate(buyTransactions);
          const buyDates = aggregatedBuys.map(agg => agg.date);
          const buyPrices = aggregatedBuys.map(agg => getPriceAtDate(agg.date) * 0.96); // Slightly below price
          const buyHoverTexts = aggregatedBuys.map(agg => {
            const header = agg.count > 1 
              ? `<b>${agg.count} transactions</b><br>Total: +${agg.totalQty} @ avg ₹${agg.avgPrice.toFixed(2)}<br>Invested: ₹${agg.totalValue.toFixed(2)}<br><br>`
              : '';
            return header + agg.details.join('<br>');
          });

          traces.push({
            x: buyDates,
            y: buyPrices,
            type: 'scatter',
            mode: 'text',
            name: 'Buy Transactions',
            text: aggregatedBuys.map(agg => `+${agg.totalQty}`),
            textposition: 'bottom center',
            textfont: { color: '#2e7d32', size: 11, family: 'Arial Black' },
            hovertemplate: '<b>BUY</b><br>Date: %{x}<br>%{customdata}<extra></extra>',
            customdata: buyHoverTexts,
            yaxis: 'y',
            showlegend: true
          });
        }

        // Add sell markers (text only, no arrows)
        if (sellTransactions.length > 0) {
          const aggregatedSells = aggregateByDate(sellTransactions);
          const sellDates = aggregatedSells.map(agg => agg.date);
          const sellPrices = aggregatedSells.map(agg => getPriceAtDate(agg.date) * 1.04); // Slightly above price
          const sellHoverTexts = aggregatedSells.map(agg => {
            const header = agg.count > 1 
              ? `<b>${agg.count} transactions</b><br>Total: -${agg.totalQty} @ avg ₹${agg.avgPrice.toFixed(2)}<br>Received: ₹${agg.totalValue.toFixed(2)}<br><br>`
              : '';
            return header + agg.details.join('<br>');
          });

          traces.push({
            x: sellDates,
            y: sellPrices,
            type: 'scatter',
            mode: 'text',
            name: 'Sell Transactions',
            text: aggregatedSells.map(agg => `-${agg.totalQty}`),
            textposition: 'top center',
            textfont: { color: '#c62828', size: 11, family: 'Arial Black' },
            hovertemplate: '<b>SELL</b><br>Date: %{x}<br>%{customdata}<extra></extra>',
            customdata: sellHoverTexts,
            yaxis: 'y',
            showlegend: true
          });
        }

        console.log(`📊 Added ${buyTransactions.length} buy and ${sellTransactions.length} sell transaction markers (aggregated by date)`);
      }
    }

    // Prepare crossover background shapes
    const crossoverShapes: any[] = [];
    if (useEnhancedData && enhancedChartData && enhancedChartData.indicators && typeof enhancedChartData.indicators === 'object') {
      // Get crossover data from indicators (use extended data for background regions)
      const goldenCrossovers = enhancedChartData.indicators['Golden Crossovers_extended'] ||
        enhancedChartData.indicators['Golden Crossovers'] ||
        enhancedChartData.indicators['Golden Crossovers (E)'] ||
        enhancedChartData.indicators['Golden Crossovers (S)'] || [];
      const deathCrossovers = enhancedChartData.indicators['Death Crossovers_extended'] ||
        enhancedChartData.indicators['Death Crossovers'] ||
        enhancedChartData.indicators['Death Crossovers (E)'] ||
        enhancedChartData.indicators['Death Crossovers (S)'] || [];

      if (goldenCrossovers.length > 0 || deathCrossovers.length > 0) {
        // Combine and sort all crossovers by date
        const allCrossovers = [
          ...goldenCrossovers.map((p: any) => ({
            date: p.date,
            price: p.value,
            type: 'golden'
          })),
          ...deathCrossovers.map((p: any) => ({
            date: p.date,
            price: p.value,
            type: 'death'
          }))
        ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        console.log(`📊 Creating crossover regions: ${goldenCrossovers.length} GCO, ${deathCrossovers.length} DCO, ${allCrossovers.length} total`);

        // Get chart date range for clipping regions
        const chartStartDate = dates.length > 0 ? dates[0] : null;
        const chartEndDate = dates.length > 0 ? dates[dates.length - 1] : null;

        // Create background shapes between crossovers
        for (let i = 0; i < allCrossovers.length; i++) {
          const currentCrossover = allCrossovers[i];
          const nextCrossover = allCrossovers[i + 1];

          // Determine start date for this region (clip to chart start)
          let regionStartDate = currentCrossover.date;
          if (chartStartDate && new Date(regionStartDate) < new Date(chartStartDate)) {
            regionStartDate = chartStartDate;
          }

          // Determine end date for this region
          let regionEndDate;
          if (nextCrossover) {
            regionEndDate = nextCrossover.date;
          } else {
            // If this is the last crossover, extend to the end of the chart
            regionEndDate = chartEndDate;
          }

          // Clip end date to chart range
          if (chartEndDate && new Date(regionEndDate) > new Date(chartEndDate)) {
            regionEndDate = chartEndDate;
          }

          // Only create shape if the region overlaps with the chart timeframe
          if (regionStartDate && regionEndDate &&
            new Date(regionStartDate) <= new Date(chartEndDate) &&
            new Date(regionEndDate) >= new Date(chartStartDate)) {

            // Determine color based on crossover type
            const isGolden = currentCrossover.type === 'golden';
            const fillColor = isGolden ? 'rgba(255, 215, 0, 0.12)' : 'rgba(255, 0, 0, 0.12)'; // Slightly more visible

            crossoverShapes.push({
              type: 'rect',
              xref: 'x',
              yref: 'paper', // Use paper coordinates for y to cover entire chart height
              x0: regionStartDate,
              y0: 0,
              x1: regionEndDate,
              y1: 1,
              fillcolor: fillColor,
              opacity: 1,
              line: { width: 0 },
              layer: 'below' // Put shapes behind traces
            });

            console.log(`📊 Created ${isGolden ? 'golden' : 'death'} crossover region: ${regionStartDate} to ${regionEndDate}`);
          }
        }
      }
    }

    // Ensure at least one subplot for the main chart
    const effectiveNumSubplots = Math.max(1, numSubplots);

    // Filter shapes to only include those within the current data range
    const dataStartDate = new Date(dates[0]);
    const dataEndDate = new Date(dates[dates.length - 1]);
    const filteredShapes = shapes.filter((shape: any) => {
      // Keep shapes that use paper coordinates (they're always valid)
      if (shape.xref === 'paper') return true;
      
      // For shapes with x coordinates, check if they're within the data range
      if (shape.x0 && shape.x1) {
        const shapeStart = new Date(shape.x0);
        const shapeEnd = new Date(shape.x1);
        
        // Keep shape if it overlaps with the data range
        return !isNaN(shapeStart.getTime()) && !isNaN(shapeEnd.getTime()) &&
               shapeEnd >= dataStartDate && shapeStart <= dataEndDate;
      }
      return true;
    });

    // Calculate dynamic height - base on main chart needs, secondary charts are small
    const legendItemCount = traces.length;
    const extraHeightForLegend = Math.max(0, (legendItemCount - 8) * 18); // Add height if many legend items
    const hasMainGroup = activeGroupEntries.some(([name]) => name === 'main');
    const numSecondaryCharts = effectiveNumSubplots - (hasMainGroup ? 1 : 0);
    // Main chart base: 500px, each secondary chart: 80px
    const baseHeight = hasMainGroup ? 550 : 400;
    const secondaryChartsHeight = numSecondaryCharts * 100;
    const calculatedHeight = baseHeight + secondaryChartsHeight + extraHeightForLegend;

    // Create layout with subplots
    const layout: any = {
      title: `${stock.symbol} - Technical Analysis (${timeframe} days) - ${numSubplots} Chart${numSubplots > 1 ? 's' : ''}`,
      xaxis: {
        title: 'Date',
        type: 'date',
        rangeslider: { visible: false }, // Hide range slider as requested
        autorange: false, // Disable autorange to prevent shapes from extending the axis
        // Always set the range to the actual data range
        range: dates.length > 0 ? [dates[0], dates[dates.length - 1]] : undefined
      },
      height: calculatedHeight, // Dynamic height based on number of subplots and legend items
      margin: { l: 80, r: 200, t: 60, b: 60 }, // Increased right margin for legend
      hovermode: 'x unified',
      showlegend: true,
      legend: {
        x: 1.01,
        y: 0.5,           // Center vertically
        xanchor: 'left',
        yanchor: 'middle', // Anchor at middle for vertical centering
        bgcolor: 'rgba(255,255,255,0.95)',
        bordercolor: 'rgba(0,0,0,0.2)',
        borderwidth: 1,
        font: { size: 11 },
        itemsizing: 'constant',
        tracegroupgap: 2,
        traceorder: 'normal'
      },
      shapes: filteredShapes // Add filtered crossover background shapes and other shapes
    };

    // Layout created with shapes

    // Create y-axes for each chart group, or at least one main axis
    // Main chart gets 3x the height of secondary charts (much more prominent)
    if (activeGroupEntries.length > 0) {
      // Calculate weighted heights: main chart = 3.0 units, others = 0.5 unit (much smaller)
      const mainGroupIndex = activeGroupEntries.findIndex(([name]) => name === 'main');
      const hasMainGroup = mainGroupIndex !== -1;
      const numSecondaryCharts = effectiveNumSubplots - (hasMainGroup ? 1 : 0);
      
      // Total weight: main gets 3.0, others get 0.5 each
      const totalWeight = hasMainGroup 
        ? 3.0 + numSecondaryCharts * 0.5 
        : effectiveNumSubplots * 1.0;
      
      // Calculate cumulative positions from bottom to top
      let currentPosition = 0;
      
      // Process groups in reverse order (bottom to top in the chart)
      const reversedEntries = [...activeGroupEntries].reverse();
      
      reversedEntries.forEach(([groupName, indicators], reverseIndex) => {
        const originalIndex = activeGroupEntries.length - 1 - reverseIndex;
        const yAxisIndex = originalIndex + 1;
        const yAxisKey = yAxisIndex === 1 ? 'yaxis' : `yaxis${yAxisIndex}`;
        
        // Main chart gets 3.0 weight, others get 0.5 (secondary charts are much smaller)
        const weight = groupName === 'main' ? 3.0 : 0.5;
        const heightFraction = weight / totalWeight;
        
        // Calculate domain for this subplot
        const domainStart = currentPosition;
        const domainEnd = currentPosition + heightFraction - 0.015; // Small gap between subplots
        currentPosition += heightFraction;

        layout[yAxisKey] = {
          title: groupName === 'main' ? '' : groupName.charAt(0).toUpperCase() + groupName.slice(1), // Hide main title to save space
          side: 'left',
          domain: [domainStart, Math.min(domainEnd, 1)],
          overlaying: undefined,
          anchor: 'x',
          // Add specific configuration for RSI group
          ...(groupName === 'momentum' && indicators.includes('RSI') && {
            range: [0, 100],
            tickmode: 'linear',
            tick0: 0,
            dtick: 25,
            showgrid: true,
            gridcolor: 'rgba(128,128,128,0.2)'
          }),
          // Format price axes
          ...(groupName === 'main' && {
            tickformat: ',.0f',
            tickprefix: '₹'
          }),
          // Make secondary chart labels smaller
          ...(groupName !== 'main' && {
            tickfont: { size: 9 }
          })
        };

        // Created y-axis for group
      });
    } else {
      // No active groups, create a default main y-axis
      layout.yaxis = {
        title: 'Price',
        domain: [0, 1],
        side: 'left',
        overlaying: undefined,
        anchor: 'x',
        tickformat: ',.0f',
        tickprefix: '₹'
      };

      // Created default main y-axis
    }

    const config = {
      responsive: true,
      displayModeBar: true,
      modeBarButtonsToRemove: ['lasso2d', 'select2d'],
      displaylogo: false,
      toImageButtonOptions: {
        format: 'png',
        filename: `${stock.symbol}_chart`,
        height: layout.height,
        width: 1200,
        scale: 1
      }
    };

    // About to render chart

    try {
      // Validate that we have valid data before clearing existing chart
      const hasValidData = traces.length > 0 && dates.length > 0;
      if (!hasValidData) {
        console.warn('⚠️ Invalid chart data, skipping render to preserve existing chart');
        setIsRenderingChart(false);
        return;
      }
      
      // Clear any existing plot first to prevent issues with multiple renders
      if (chartRef.current && (chartRef.current as any)._fullLayout) {
        console.log('🧹 Clearing existing plot before re-rendering');
        Plotly.purge(chartRef.current);
      }
      
      Plotly.newPlot(chartRef.current, traces as any, layout as any, config as any);

      const chartTime = performance.now() - startTime;
      console.log(`✅ Chart rendered successfully in ${chartTime.toFixed(2)}ms`);
    } catch (error) {
      console.error('❌ Chart rendering failed:', error);
      console.log('📊 Failed chart data:', { traces: traces.length, layout, config });
    } finally {
      setIsRenderingChart(false);
    }
  };

  const handleTechnicalChange = (event: SelectChangeEvent<typeof selectedTechnicals>) => {
    const value = event.target.value;
    setSelectedTechnicals(typeof value === 'string' ? value.split(',') : value);
  };

  // Chart Groups Management Functions
  const handleDragStart = (e: React.DragEvent, indicator: string) => {
    setDraggedIndicator(indicator);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetGroup: string) => {
    e.preventDefault();
    if (!draggedIndicator) return;

    // Find source group
    let sourceGroup = '';
    for (const [groupName, indicators] of Object.entries(chartGroups)) {
      if (indicators.includes(draggedIndicator)) {
        sourceGroup = groupName;
        break;
      }
    }

    if (sourceGroup && sourceGroup !== targetGroup) {
      setChartGroups(prev => ({
        ...prev,
        [sourceGroup]: prev[sourceGroup].filter(ind => ind !== draggedIndicator),
        [targetGroup]: [...prev[targetGroup], draggedIndicator]
      }));
      toast.success(`Moved ${draggedIndicator} to ${targetGroup} chart`);
    }

    setDraggedIndicator(null);
  };

  const toggleGroupActive = (groupName: string) => {
    setActiveGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const createNewGroup = () => {
    if (!newGroupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    if (chartGroups[newGroupName.toLowerCase()]) {
      toast.error('Group already exists');
      return;
    }

    setChartGroups(prev => ({
      ...prev,
      [newGroupName.toLowerCase()]: []
    }));

    setActiveGroups(prev => ({
      ...prev,
      [newGroupName.toLowerCase()]: false
    }));

    setNewGroupName('');
    toast.success(`Created new group: ${newGroupName}`);
  };

  const deleteGroup = (groupName: string) => {
    if (groupName === 'main') {
      toast.error('Cannot delete main group');
      return;
    }

    // Move indicators back to main group
    const indicators = chartGroups[groupName];
    setChartGroups(prev => {
      const newGroups = { ...prev };
      if (indicators && indicators.length > 0) {
        newGroups.main = [...newGroups.main, ...indicators];
      }
      delete newGroups[groupName];
      return newGroups;
    });

    setActiveGroups(prev => {
      const newActive = { ...prev };
      delete newActive[groupName];
      return newActive;
    });

    toast.success(`Deleted group: ${groupName}`);
  };

  const startRenaming = (groupName: string) => {
    if (groupName === 'main') {
      toast.error('Cannot rename main group');
      return;
    }
    setRenamingGroup(groupName);
    setRenameValue(groupName);
  };

  const finishRenaming = () => {
    if (!renamingGroup || !renameValue.trim()) {
      setRenamingGroup(null);
      setRenameValue('');
      return;
    }

    const newName = renameValue.trim().toLowerCase();
    if (newName === renamingGroup) {
      setRenamingGroup(null);
      setRenameValue('');
      return;
    }

    if (chartGroups[newName]) {
      toast.error('Group name already exists');
      return;
    }

    // Rename the group
    const newGroups = { ...chartGroups };
    newGroups[newName] = newGroups[renamingGroup];
    delete newGroups[renamingGroup];
    setChartGroups(newGroups);

    // Update active groups
    const newActiveGroups = { ...activeGroups };
    newActiveGroups[newName] = newActiveGroups[renamingGroup];
    delete newActiveGroups[renamingGroup];
    setActiveGroups(newActiveGroups);

    setRenamingGroup(null);
    setRenameValue('');
    toast.success(`Renamed group to: ${newName}`);
  };

  const cancelRenaming = () => {
    setRenamingGroup(null);
    setRenameValue('');
  };

  // Helper function for RSI calculation (fallback)
  const calculateRSI = (prices: number[], period: number = 14): number[] => {
    if (prices.length < period + 1) return [];

    const rsi: number[] = [];
    const gains: number[] = [];
    const losses: number[] = [];

    // Calculate price changes
    for (let i = 1; i < prices.length; i++) {
      const change = prices[i] - prices[i - 1];
      gains.push(change > 0 ? change : 0);
      losses.push(change < 0 ? Math.abs(change) : 0);
    }

    // Calculate initial average gain and loss
    let avgGain = gains.slice(0, period).reduce((a, b) => a + b, 0) / period;
    let avgLoss = losses.slice(0, period).reduce((a, b) => a + b, 0) / period;

    // Add NaN values for the first 'period' data points
    for (let i = 0; i < period; i++) {
      rsi.push(NaN);
    }

    // Calculate RSI for remaining data points
    for (let i = period; i < gains.length; i++) {
      if (avgLoss === 0) {
        rsi.push(100);
      } else {
        const rs = avgGain / avgLoss;
        rsi.push(100 - (100 / (1 + rs)));
      }

      // Update averages using Wilder's smoothing
      avgGain = ((avgGain * (period - 1)) + gains[i]) / period;
      avgLoss = ((avgLoss * (period - 1)) + losses[i]) / period;
    }

    return rsi;
  };

  // Helper function for SMA calculation (fallback)
  const calculateSMA = (prices: number[], period: number): number[] => {
    if (prices.length < period) return [];

    const sma: number[] = [];

    // Add NaN values for the first 'period-1' data points
    for (let i = 0; i < period - 1; i++) {
      sma.push(NaN);
    }

    // Calculate SMA for remaining data points
    for (let i = period - 1; i < prices.length; i++) {
      const sum = prices.slice(i - period + 1, i + 1).reduce((a, b) => a + b, 0);
      sma.push(sum / period);
    }

    return sma;
  };

  // Helper function for EMA calculation (fallback)
  const calculateEMA = (prices: number[], period: number): number[] => {
    if (prices.length < period) return [];

    const ema: number[] = [];
    const multiplier = 2 / (period + 1);

    // Add NaN values for the first 'period-1' data points
    for (let i = 0; i < period - 1; i++) {
      ema.push(NaN);
    }

    // Start with SMA for the first EMA value
    const firstSMA = prices.slice(0, period).reduce((a, b) => a + b, 0) / period;
    ema.push(firstSMA);

    // Calculate EMA for remaining data points
    for (let i = period; i < prices.length; i++) {
      const currentEMA = (prices[i] * multiplier) + (ema[i - 1] * (1 - multiplier));
      ema.push(currentEMA);
    }

    return ema;
  };

  // Helper function for crossover calculation (fallback)
  const calculateCrossovers = (ema50: number[], ema200: number[], dates: string[], closes: number[]) => {
    const golden: Array<{ date: string, price: number }> = [];
    const death: Array<{ date: string, price: number }> = [];

    // Need at least 2 points to detect crossovers
    if (ema50.length < 2 || ema200.length < 2) return { golden, death };

    for (let i = 1; i < Math.min(ema50.length, ema200.length, dates.length, closes.length); i++) {
      const prevEma50 = ema50[i - 1];
      const currEma50 = ema50[i];
      const prevEma200 = ema200[i - 1];
      const currEma200 = ema200[i];

      // Skip if any values are NaN
      if (isNaN(prevEma50) || isNaN(currEma50) || isNaN(prevEma200) || isNaN(currEma200)) {
        continue;
      }

      // Golden Crossover: EMA50 crosses above EMA200
      if (prevEma50 <= prevEma200 && currEma50 > currEma200) {
        golden.push({
          date: dates[i],
          price: closes[i]
        });
      }

      // Death Crossover: EMA50 crosses below EMA200
      if (prevEma50 >= prevEma200 && currEma50 < currEma200) {
        death.push({
          date: dates[i],
          price: closes[i]
        });
      }
    }

    return { golden, death };
  };

  // Helper function for basic support/resistance calculation (fallback)
  const calculateSupportResistance = (highs: number[], lows: number[], closes: number[]) => {
    if (highs.length < 20) return { support: null, resistance: null };

    // Simple support/resistance calculation
    // Support: lowest low in recent period
    // Resistance: highest high in recent period
    const recentPeriod = Math.min(50, highs.length);
    const recentHighs = highs.slice(-recentPeriod);
    const recentLows = lows.slice(-recentPeriod);

    const resistance = Math.max(...recentHighs);
    const support = Math.min(...recentLows);

    return { support, resistance };
  };

  // Load stock adjustments
  const loadAdjustments = async () => {
    try {
      const data = await stockService.getStockAdjustments(stock.symbol);
      setAdjustments(data);
    } catch (error: any) {
      console.error('Failed to load adjustments:', error);
      // Don't show error toast, just log it
    }
  };

  const loadStockData = async () => {
    console.log('📊 Loading data with auto-update for:', stock.symbol, 'timeframe:', timeframe);

    // Check cache first for chart data
    const cacheKey = `${stock.symbol}_${timeframe}`;
    if (dataCache[cacheKey] && chartDataLoaded) {
      console.log('📦 Using cached data for:', cacheKey);
      const cachedData = dataCache[cacheKey];
      setStockPrices(cachedData.price_data || []);
      setLatestPrice(cachedData.latest_price);
      setEnhancedChartData(cachedData.enhanced_data);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Use the new auto-update endpoint that checks and updates stale data
      let stockDetailsData = await stockService.getStockDetailsWithUpdate(stock.symbol);

      console.log('✅ Loaded stock details with auto-update:', stockDetailsData);
      console.log('🔍 Raw response keys:', Object.keys(stockDetailsData || {}));
      console.log('🔍 Raw response type:', typeof stockDetailsData);

      // Fix: If response is a string, parse it as JSON
      if (typeof stockDetailsData === 'string') {
        console.log('🔧 Response is string, parsing as JSON...');
        try {
          // First, try to clean up common JSON issues
          let cleanedJson = stockDetailsData;
          
          // Replace NaN with null (common issue)
          cleanedJson = cleanedJson.replace(/:\s*NaN\s*([,}])/g, ': null$1');
          
          // Replace Infinity with null
          cleanedJson = cleanedJson.replace(/:\s*Infinity\s*([,}])/g, ': null$1');
          cleanedJson = cleanedJson.replace(/:\s*-Infinity\s*([,}])/g, ': null$1');
          
          stockDetailsData = JSON.parse(cleanedJson);
          console.log('✅ Successfully parsed JSON response:', stockDetailsData);
        } catch (parseError) {
          console.error('❌ Failed to parse JSON response:', parseError);
          console.error('❌ Raw response (first 500 chars):', stockDetailsData.substring(0, 500));
          throw new Error('Invalid JSON response from server');
        }
      }

      // Extract data from the response
      const { data_status, price_data, latest_price, technical_analysis } = stockDetailsData;
      
      console.log('🔍 Destructured data check:', {
        data_status,
        price_data_exists: !!price_data,
        price_data_type: typeof price_data,
        price_data_isArray: Array.isArray(price_data),
        price_data_length: price_data?.length,
        latest_price_exists: !!latest_price,
        technical_analysis_exists: !!technical_analysis
      });

      // Show auto-update status to user (with safe checks)
      if (data_status && data_status.update_triggered) {
        if (data_status.update_type === 'incremental') {
          toast.info(`📅 Stock data was ${data_status.days_to_update} days behind. Update started in background.`);
        } else if (data_status.update_type === 'full') {
          toast.info(`📊 No historical data found. Full data download started in background.`);
        }
      } else if (data_status && data_status.status === 'current') {
        console.log(`✅ Data for ${stock.symbol} is current (last: ${data_status.last_date})`);
      }

      // Set the price data (filter by timeframe if needed)
      console.log('🔍 Debug price_data check:', {
        price_data_exists: !!price_data,
        price_data_type: typeof price_data,
        price_data_isArray: Array.isArray(price_data),
        price_data_length: price_data?.length,
        price_data_sample: price_data?.slice(0, 2)
      });
      
      if (price_data && Array.isArray(price_data) && price_data.length > 0) {
        // Convert and filter data by timeframe
        let filteredData = price_data;
        if (timeframe < price_data.length) {
          filteredData = price_data.slice(-timeframe);
        }

        console.log('📊 Price data loaded:', filteredData.length, 'records for', stock.symbol);
        setStockPrices(filteredData);
      } else {
        console.warn('⚠️ No price data available for', stock.symbol, {
          price_data_exists: !!price_data,
          price_data_type: typeof price_data,
          price_data_length: price_data?.length,
          full_response: stockDetailsData
        });
        setStockPrices([]);
        if (data_status && data_status.status === 'error') {
          setError(`Error loading data: ${data_status.error}`);
        } else if (data_status && !data_status.update_triggered) {
          setError(`No price data available for ${stock.symbol}. This stock may not be actively tracked.`);
        }
      }

      // Set latest price if available
      if (latest_price && typeof latest_price.close === 'number' && !isNaN(latest_price.close)) {
        const latestPriceData: StockPrice = {
          id: 1,
          stock_id: 1,
          stock_symbol: stock.symbol,
          price: latest_price.close,
          change: 0, // Will be calculated if needed
          change_percent: 0,
          volume: latest_price.volume || 0,
          timestamp: latest_price.date
        };
        setLatestPrice(latestPriceData);
      } else {
        setLatestPrice(null);
      }

      // Set technical analysis if available (from consolidated file)
      if (technical_analysis) {
        console.log('📊 Raw technical analysis data from consolidated source:', technical_analysis);
        const normalizedAnalysis = normalizeTechnicalAnalysis(technical_analysis);
        console.log('📊 Normalized technical analysis from consolidated data:', normalizedAnalysis);
        setTechnicalAnalysis(normalizedAnalysis);
        setTechnicalAnalysisLoaded(true);
      }

      // Cache the data for future use
      const cacheData = {
        price_data: price_data || [],
        latest_price: latest_price,
        technical_analysis: technical_analysis,
        timestamp: Date.now()
      };
      setDataCache(prev => ({ ...prev, [cacheKey]: cacheData }));
      setChartDataLoaded(true);

    } catch (error: any) {
      console.error('❌ Failed to load stock data for', stock.symbol, ':', error.message);

      if (error.response?.status === 429) {
        setError('Rate limit exceeded. Please wait a moment before trying again.');
        toast.error('Too many requests. Please wait a moment.');
      } else if (error.message?.includes('No price data available')) {
        setError(`No price data available for ${stock.symbol}. This stock may not be actively tracked.`);
      } else {
        setError(error.message || 'Failed to load stock data');
        toast.error('Failed to load stock data');
      }

      setStockPrices([]);
      setLatestPrice(null);
    } finally {
      setLoading(false);
    }
  };

  const loadEventHistory = async () => {
    try {
      setEventHistoryLoading(true);
      const response = await stockService.getStockEventHistory(stock.symbol);
      setEventHistory(response.impacts || []);
      setEventSummary(response.summary || null);
    } catch (error: any) {
      console.error('Failed to load event history:', error);
    } finally {
      setEventHistoryLoading(false);
    }
  };

  const loadInvestmentProfile = async () => {
    try {
      // Load value score
      const valueResponse = await stockService.getStockValueScore(stock.symbol);
      if (valueResponse.score) {
        setValueScore(valueResponse.score);
      }
      
      // Load swing signals
      const swingResponse = await stockService.getStockSwingSignals(stock.symbol);
      setSwingSignals(swingResponse.signals || []);
    } catch (error: any) {
      console.error('Failed to load investment profile:', error);
    }
  };

  const loadTechnicalAnalysis = async () => {
    try {
      setAnalysisLoading(true);
      console.log('📊 Loading technical analysis for:', stock.symbol);
      const analysis = await stockService.getTechnicalAnalysis(stock.symbol);
      console.log('📊 Technical analysis data received:', analysis);
      const normalizedAnalysis = normalizeTechnicalAnalysis(analysis);
      setTechnicalAnalysis(normalizedAnalysis);
      setTechnicalAnalysisLoaded(true); // Mark analysis as loaded
      console.log('📊 Normalized technical analysis from API:', normalizedAnalysis);
    } catch (error: any) {
      console.error('Failed to load technical analysis:', error);
      // Don't show error toast for technical analysis - it's optional
    } finally {
      setAnalysisLoading(false);
    }
  };

  const runTechnicalAnalysisForStock = async () => {
    try {
      setAnalysisLoading(true);
      toast.info('Running technical analysis...');

      // Trigger analysis for this specific stock
      await stockService.runTechnicalAnalysis(stock.symbol);

      // Wait a moment then reload the analysis
      setTimeout(async () => {
        await loadTechnicalAnalysis();
        toast.success('Technical analysis completed!');
      }, 2000);

    } catch (error: any) {
      console.error('Failed to run technical analysis:', error);
      toast.error('Failed to run technical analysis');
      setAnalysisLoading(false);
    }
  };

  const handleSwingAnalysis = async () => {
    if (!peakPct && !troughPct) {
      toast.error('Please enter Peak % or Trough %');
      return;
    }

    try {
      setSwingAnalysisLoading(true);

      const peakPctNum = peakPct ? parseInt(peakPct) : undefined;
      const troughPctNum = troughPct ? parseInt(troughPct) : undefined;

      if ((peakPct && isNaN(peakPctNum!)) || (troughPct && isNaN(troughPctNum!))) {
        toast.error('Please enter valid numbers for Peak % and Trough %');
        return;
      }

      console.log('📊 Calculating custom swing points:', { peakPct: peakPctNum, troughPct: troughPctNum, timeframe });

      const result = await stockService.calculateCustomSwingPoints(
        stock.symbol,
        peakPctNum || troughPctNum!,
        troughPctNum || peakPctNum!,
        timeframe
      );

      console.log('📊 Swing analysis result:', result);
      setCustomSwingData(result);

      // Chart will re-render automatically via useEffect when customSwingData changes

      toast.success(`Swing analysis complete: ${result.statistics[`nSwing_${result.peak_pct}_${result.trough_pct}`]} swing points found`);

    } catch (error: any) {
      console.error('❌ Swing analysis failed:', error);
      toast.error(error.response?.data?.error || 'Failed to calculate swing analysis');
    } finally {
      setSwingAnalysisLoading(false);
    }
  };

  const clearSwingAnalysis = () => {
    setCustomSwingData(null);
    setPeakPct('');
    setTroughPct('');
    // Re-render chart without custom swing points (only if we have price data)
    if (stockPrices.length > 0 || (enhancedChartData && enhancedChartData.data && enhancedChartData.data.length > 0)) {
      createChart();
    }
  };

  const loadFundamentalData = async () => {
    if (fundamentalDataLoaded || fundamentalData) {
      console.log('📊 Fundamental data already loaded, skipping');
      return;
    }

    console.log('📊 Loading fundamental data for:', stock.symbol);
    try {
      // Use stockService to get fundamental data with proper authentication
      const response = await stockService.getFundamentalData(stock.symbol);
      console.log('📊 Fundamental data loaded:', response);
      setFundamentalData(response.fundamentals || response.data || response);
      setFundamentalDataLoaded(true);
    } catch (error: any) {
      console.error('❌ Error loading fundamental data:', error);
      setFundamentalData(null);
      setFundamentalDataLoaded(true); // Mark as loaded even if failed to prevent retry loops
    }
  };

  const handleInstantScraping = async () => {
    console.log('🔍 Starting instant scraping for:', stock.symbol);

    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to use the scraping feature');
      return;
    }

    setScrapingLoading(true);
    setScrapingResult(null);

    try {
      const response = await stockService.scrapeSpecificStock(stock.symbol);
      console.log('✅ Instant scraping completed:', response);

      setScrapingResult(response);

      // Show success message
      toast.success(`✅ Successfully scraped data for ${stock.symbol}`);

      // Refresh fundamental data to show the newly scraped data
      setFundamentalDataLoaded(false);
      setTimeout(() => {
        loadFundamentalData();
      }, 1000); // Wait 1 second for the scraping to complete

    } catch (error: any) {
      console.error('❌ Instant scraping failed:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config
      });

      const errorMessage = error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        `Failed to scrape data for ${stock.symbol}`;

      toast.error(errorMessage);
      setScrapingResult({
        success: false,
        error: errorMessage,
        details: error.response?.data
      });
    } finally {
      setScrapingLoading(false);
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '₹-';
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  // Removed unused formatPercentage function

  // Helper function to normalize technical analysis data from different sources
  const normalizeTechnicalAnalysis = (data: any): TechnicalAnalysis | null => {
    if (!data) return null;

    // Handle both PascalCase (from consolidated data) and snake_case (from API) formats
    return {
      id: data.id || '',
      stock_id: data.stock_id || '',
      stock_symbol: data.stock_symbol || data.Stockname || stock.symbol,
      analysis_date: data.analysis_date || data.LastTradeDate || '',
      last_trade_price: data.last_trade_price || data.LastTradePrice || 0,
      lifetime_high: data.lifetime_high || data.LifetimeHigh || 0,
      lifetime_low: data.lifetime_low || data.LifetimeLow || 0,
      one_year_high: data.one_year_high || data.OneYearHigh || data.Week52High || 0,
      one_year_low: data.one_year_low || data.OneYearLow || data.Week52Low || 0,
      month_high: data.month_high || data.MonthHigh || 0,
      month_low: data.month_low || data.MonthLow || 0,
      week_high: data.week_high || data.WeekHigh || 0,
      week_low: data.week_low || data.WeekLow || 0,
      one_year_return: data.one_year_return || data.OneYearReturn || 0,
      month_return: data.month_return || data.MonthReturn || 0,
      week_return: data.week_return || data.WeekReturn || 0,
      day_return: data.day_return || data.DayReturn || 0,
      last_golden_crossover: data.last_golden_crossover || data.LastGoldenCrossover || null,
      last_death_crossover: data.last_death_crossover || data.LastDeathCrossover || null,
      gco_change: data.gco_change || data.GCOChange || 0,
      dco_change: data.dco_change || data.DCOChange || 0,
      gco_hit_rate: data.gco_hit_rate || data.GCOHitRate || 0,
      gco_avg_return: data.gco_avg_return || data.GCOAvgReturn || 0,
      gco_count: data.gco_count || data.TotalGCOs || 0,
      gco_return_std_dev: data.gco_return_std_dev || data.GCOReturnStdDev || 0,
      gco_acce_point: data.gco_acce_point || data.GCOAccePoint || 0,
      gco_acce_hit_rate: data.gco_acce_hit_rate || data.GCOAcceHitRate || 0,
      dco_count: data.dco_count || data.TotalDCOs || 0,
      n_swing_10: data.n_swing_10 || data.NSwing10 || 0,
      d_swing_10: data.d_swing_10 || data.DSwing10 || 0,
      v_swing_10: data.v_swing_10 || data.VSwing10 || 0,
      n_swing_15: data.n_swing_15 || data.NSwing15 || 0,
      d_swing_15: data.d_swing_15 || data.DSwing15 || 0,
      v_swing_15: data.v_swing_15 || data.VSwing15 || 0,
      n_swing_20: data.n_swing_20 || data.NSwing20 || 0,
      d_swing_20: data.d_swing_20 || data.DSwing20 || 0,
      v_swing_20: data.v_swing_20 || data.VSwing20 || 0,
      market_cap: data.market_cap || data.MarketCap || 0,
      created_at: data.created_at || '',
      updated_at: data.updated_at || ''
    };
  };

  const priceChange = latestPrice && typeof latestPrice.change === 'number' ? latestPrice.change : 0;
  const priceChangePercent = latestPrice && typeof latestPrice.change_percent === 'number' ? latestPrice.change_percent : 0;

  // Helper function to safely format numbers
  const safeToFixed = (value: any, decimals: number = 2): string => {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'number' && !isNaN(value)) {
      return value.toFixed(decimals);
    }
    return '-';
  };

  // Helper function to safely display counts
  const safeCount = (value: any): string => {
    if (value === null || value === undefined) {
      return '0';
    }
    if (typeof value === 'number' && !isNaN(value)) {
      return value.toString();
    }
    return '0';
  };

  // Helper function to get market cap from various sources
  const getMarketCap = (): string => {
    // Try to get from technical analysis first
    if (technicalAnalysis?.market_cap && typeof technicalAnalysis.market_cap === 'number') {
      return formatCurrency(technicalAnalysis.market_cap);
    }

    // Try to get from latest price data
    if (latestPrice?.market_cap && typeof latestPrice.market_cap === 'number') {
      return formatCurrency(latestPrice.market_cap);
    }

    // Try to get from fundamental data
    if (fundamentalData?.significant_values) {
      const marketCapKeys = ['Market Cap', 'market_cap', 'Market Capitalisation', 'Mkt Cap'];
      for (const key of marketCapKeys) {
        const value = fundamentalData.significant_values[key];
        if (value && typeof value === 'string') {
          return value; // Already formatted from scraped data
        }
        if (value && typeof value === 'number') {
          return formatCurrency(value);
        }
      }
    }

    return '-';
  };

  // Helper function to get P/E ratio from various sources
  const getPERatio = (): string => {
    // Try to get from fundamental data first
    if (fundamentalData?.significant_values) {
      const peKeys = ['Stock P/E', 'PE Ratio', 'P/E', 'pe_ratio', 'Stock PE'];
      for (const key of peKeys) {
        const value = fundamentalData.significant_values[key];
        if (value && typeof value === 'string') {
          return value; // Already formatted from scraped data
        }
        if (value && typeof value === 'number') {
          return safeToFixed(value, 2);
        }
      }
    }

    // Try to get from latest price data
    if (latestPrice?.pe_ratio && typeof latestPrice.pe_ratio === 'number') {
      return safeToFixed(latestPrice.pe_ratio, 2);
    }

    return '-';
  };

  // Helper function to get 52-week high percentage down
  const getDownFrom52wHigh = (): string => {
    if (fundamentalData?.significant_values) {
      const downKeys = ['Down from 52w high', 'Down from 52w High', 'down_from_52w_high'];
      for (const key of downKeys) {
        const value = fundamentalData.significant_values[key];
        if (value && typeof value === 'string') {
          return value.includes('%') ? value : `${value}%`;
        }
        if (value && typeof value === 'number') {
          return `${safeToFixed(value, 1)}%`;
        }
      }
    }

    // Calculate from technical analysis if available
    if (technicalAnalysis?.one_year_high && latestPrice?.price) {
      const downPercent = ((technicalAnalysis.one_year_high - latestPrice.price) / technicalAnalysis.one_year_high) * 100;
      return `${safeToFixed(downPercent, 1)}%`;
    }

    return '-';
  };

  // Helper function to get 52-week low percentage up
  const getUpFrom52wLow = (): string => {
    if (fundamentalData?.significant_values) {
      const upKeys = ['Up from 52w low', 'Up from 52w Low', 'up_from_52w_low'];
      for (const key of upKeys) {
        const value = fundamentalData.significant_values[key];
        if (value && typeof value === 'string') {
          return value.includes('%') ? value : `${value}%`;
        }
        if (value && typeof value === 'number') {
          return `${safeToFixed(value, 1)}%`;
        }
      }
    }

    // Calculate from technical analysis if available
    if (technicalAnalysis?.one_year_low && latestPrice?.price) {
      const upPercent = ((latestPrice.price - technicalAnalysis.one_year_low) / technicalAnalysis.one_year_low) * 100;
      return `${safeToFixed(upPercent, 1)}%`;
    }

    return '-';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={onBack} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                {stock.symbol}
              </Typography>
              <Tooltip title="View on Screener.in">
                <IconButton
                  size="small"
                  component="a"
                  href={`https://www.screener.in/company/${stock.symbol.replace(/\.(NS|BO)$/, '')}/consolidated/`}
                  target="_blank"
                  rel="noopener"
                  sx={{ p: 0.25 }}
                >
                  <OpenInNewIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" color="textSecondary">
              {stock.exchange || 'Unknown Exchange'}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadStockData}
            disabled={loading}
          >
            Refresh
          </Button>
          <IconButton
            onClick={() => onToggleWatchlist(stock)}
            color={isInWatchlist ? 'primary' : 'default'}
            size="medium"
          >
            {isInWatchlist ? <Star /> : <StarBorder />}
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Price Summary Card */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <Typography variant="h4" component="div" sx={{ fontWeight: 600 }}>
                {latestPrice ? formatCurrency(latestPrice.price) : '-'}
              </Typography>
              {latestPrice && (
                <Box display="flex" alignItems="center" mt={0.5}>
                  {priceChange >= 0 ? (
                    <TrendingUp color="success" fontSize="small" />
                  ) : (
                    <TrendingDown color="error" fontSize="small" />
                  )}
                  <Typography
                    variant="body2"
                    color={priceChange >= 0 ? 'success.main' : 'error.main'}
                    sx={{ ml: 0.5, fontWeight: 500 }}
                  >
                    {priceChange >= 0 ? '+' : ''}{safeToFixed(priceChange, 2)} ({safeToFixed(priceChangePercent, 2)}%)
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={9}>
              <Box display="flex" justifyContent="space-around" alignItems="center">
                <Box textAlign="center">
                  <Typography variant="caption" color="textSecondary">Volume</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {latestPrice ? formatNumber(latestPrice.volume) : '-'}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="caption" color="textSecondary">Market Cap</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {getMarketCap()}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="caption" color="textSecondary">P/E Ratio</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {getPERatio()}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="caption" color="textSecondary">52W High</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {technicalAnalysis?.one_year_high ? formatCurrency(technicalAnalysis.one_year_high) : '-'}
                  </Typography>
                  <Typography variant="caption" color="error.main" display="block">
                    -{getDownFrom52wHigh()}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="caption" color="textSecondary">52W Low</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {technicalAnalysis?.one_year_low ? formatCurrency(technicalAnalysis.one_year_low) : '-'}
                  </Typography>
                  <Typography variant="caption" color="success.main" display="block">
                    +{getUpFrom52wLow()}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Chart Controls */}
      <Card sx={{ mb: 1 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Timeframe Selector */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>Timeframe</Typography>
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                {[
                  { days: 7, label: '1W' },
                  { days: 30, label: '1M' },
                  { days: 90, label: '3M' },
                  { days: 180, label: '6M' },
                  { days: 365, label: '1Y' },
                  { days: 730, label: '2Y' },
                  { days: 1825, label: '5Y' },
                  { days: 3650, label: 'Max' }
                ].map((period) => (
                  <Chip
                    key={period.days}
                    label={period.label}
                    onClick={() => setTimeframe(period.days)}
                    color={timeframe === period.days ? 'primary' : 'default'}
                    variant={timeframe === period.days ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
                
                {/* Transaction Markers Toggle - moved here for visibility */}
                <Tooltip title={stockTransactions.length > 0 
                  ? `${showTransactionMarkers ? 'Hide' : 'Show'} buy/sell transaction markers (${stockTransactions.length} transactions)`
                  : 'No transactions found for this stock'
                }>
                  <span>
                    <Chip
                      label={`📊 Txns${stockTransactions.length > 0 ? ` (${stockTransactions.length})` : ''}`}
                      onClick={() => setShowTransactionMarkers(!showTransactionMarkers)}
                      disabled={stockTransactions.length === 0}
                      color={showTransactionMarkers && stockTransactions.length > 0 ? 'success' : 'default'}
                      variant={showTransactionMarkers && stockTransactions.length > 0 ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ cursor: stockTransactions.length > 0 ? 'pointer' : 'not-allowed' }}
                    />
                  </span>
                </Tooltip>
              </Box>
            </Grid>

            {/* Technical Indicators Selector */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Technical Indicators</InputLabel>
                <Select
                  multiple
                  value={selectedTechnicals}
                  onChange={handleTechnicalChange}
                  input={<OutlinedInput label="Technical Indicators" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 224,
                        width: 250,
                      },
                    },
                  }}
                >
                  {TECHNICAL_INDICATORS.map((indicator) => (
                    <MenuItem key={indicator} value={indicator}>
                      <Checkbox checked={selectedTechnicals.indexOf(indicator) > -1} />
                      <ListItemText primary={indicator} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Chart Configuration */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Chart Type</InputLabel>
                  <Select
                    value={chartType}
                    label="Chart Type"
                    onChange={(e) => setChartType(e.target.value as 'line' | 'candlestick')}
                  >
                    <MenuItem value="line">Line Chart</MenuItem>
                    <MenuItem value="candlestick">Candlestick</MenuItem>
                  </Select>
                </FormControl>


                {/* S/R Level Count Control */}
                {selectedTechnicals.includes('Support/Resistance') && (
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>S/R Levels</InputLabel>
                    <Select
                      value={srLevelCount}
                      label="S/R Levels"
                      onChange={(e) => setSrLevelCount(Number(e.target.value))}
                    >
                      <MenuItem value={1}>1 each</MenuItem>
                      <MenuItem value={2}>2 each</MenuItem>
                      <MenuItem value={3}>3 each</MenuItem>
                      <MenuItem value={4}>4 each</MenuItem>
                      <MenuItem value={5}>5 each</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {/* S/R Help Button */}
                {selectedTechnicals.includes('Support/Resistance') && (
                  <Tooltip title="Learn how Support/Resistance levels are calculated">
                    <IconButton
                      size="small"
                      onClick={() => setShowSRHelpModal(true)}
                      sx={{ color: 'info.main' }}
                    >
                      <HelpOutlineIcon />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Drag indicators between groups to organize charts. Toggle groups on/off to control which charts are displayed.">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowChartConfig(!showChartConfig)}
                  >
                    {showChartConfig ? 'Hide' : 'Show'} Chart Groups
                  </Button>
                </Tooltip>
              </Box>
            </Grid>

            {/* Enhanced Chart Groups Configuration */}
            {showChartConfig && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2 }}>

                  {/* Create New Group */}
                  <Box sx={{ mb: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder="Group name"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && createNewGroup()}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={createNewGroup}
                        disabled={!newGroupName.trim()}
                      >
                        Create
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          // Clear localStorage to force fresh defaults
                          localStorage.removeItem(`chartGroups_${stock.symbol}`);
                          localStorage.removeItem(`activeGroups_${stock.symbol}`);
                          localStorage.removeItem(`selectedTechnicals_${stock.symbol}`);

                          const newDefaults = ['Close Price', 'EMA50', 'EMA200', 'Golden Crossovers (E)', 'Death Crossovers (E)', 'Volume'];
                          setChartGroups(getDefaultChartGroups());
                          setActiveGroups(getDefaultActiveGroups());
                          setSelectedTechnicals(newDefaults);

                          console.log('📊 Reset to defaults:', {
                            chartGroups: getDefaultChartGroups(),
                            activeGroups: getDefaultActiveGroups(),
                            selectedTechnicals: newDefaults
                          });

                          toast.success('Reset to default chart groups and indicators');
                        }}
                        sx={{ ml: 1 }}
                      >
                        Reset to Defaults
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        onClick={() => {
                          // Force clear ALL localStorage for this stock
                          const keys = Object.keys(localStorage).filter(key => key.includes(stock.symbol));
                          keys.forEach(key => localStorage.removeItem(key));

                          // Force reload the page to get fresh state
                          window.location.reload();
                        }}
                        sx={{ ml: 1 }}
                      >
                        Force Reset & Reload
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        color="success"
                        onClick={() => setShowSaveViewDialog(true)}
                        sx={{ ml: 1 }}
                      >
                        Save View
                      </Button>
                    </Box>
                    
                    {/* Saved Views Section */}
                    {Object.keys(savedViews).length > 0 && (
                      <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                          Saved Views:
                        </Typography>
                        {Object.keys(savedViews).map((viewName) => (
                          <Chip
                            key={viewName}
                            label={viewName}
                            onClick={() => loadSavedView(viewName)}
                            onDelete={() => deleteSavedView(viewName)}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                    )}
                    
                    {/* Save View Dialog */}
                    <Dialog open={showSaveViewDialog} onClose={() => setShowSaveViewDialog(false)}>
                      <DialogTitle>Save Chart View</DialogTitle>
                      <DialogContent>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          Save the current chart group configuration as a named view that can be applied to any stock.
                        </Typography>
                        <TextField
                          autoFocus
                          fullWidth
                          label="View Name"
                          value={saveViewName}
                          onChange={(e) => setSaveViewName(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && saveCurrentView(saveViewName)}
                          placeholder="e.g., My Trading Setup"
                        />
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={() => setShowSaveViewDialog(false)}>Cancel</Button>
                        <Button 
                          onClick={() => saveCurrentView(saveViewName)} 
                          variant="contained" 
                          color="success"
                          disabled={!saveViewName.trim()}
                        >
                          Save
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </Box>

                  <Grid container spacing={2}>
                    {Object.entries(chartGroups).map(([groupName, indicators]) => (
                      <Grid item xs={12} sm={6} md={3} key={groupName}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            border: activeGroups[groupName] ? '2px solid #1976d2' : '1px solid #e0e0e0',
                            bgcolor: activeGroups[groupName] ? 'primary.50' : 'background.paper',
                            minHeight: 120,
                            position: 'relative'
                          }}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, groupName)}
                        >
                          {/* Group Header with Controls */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            {renamingGroup === groupName ? (
                              <TextField
                                size="small"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') finishRenaming();
                                  if (e.key === 'Escape') cancelRenaming();
                                }}
                                onBlur={finishRenaming}
                                autoFocus
                                sx={{ flexGrow: 1, mr: 1 }}
                              />
                            ) : (
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 'bold',
                                  textTransform: 'capitalize',
                                  cursor: groupName !== 'main' ? 'pointer' : 'default'
                                }}
                                onClick={() => groupName !== 'main' && startRenaming(groupName)}
                                title={groupName !== 'main' ? 'Click to rename' : ''}
                              >
                                {groupName} Chart
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Checkbox
                                size="small"
                                checked={activeGroups[groupName] || false}
                                onChange={() => toggleGroupActive(groupName)}
                                title={`Toggle ${groupName} chart`}
                              />
                              {groupName !== 'main' && (
                                <IconButton
                                  size="small"
                                  onClick={() => deleteGroup(groupName)}
                                  title={`Delete ${groupName} group`}
                                  sx={{ color: 'error.main' }}
                                >
                                  ×
                                </IconButton>
                              )}
                            </Box>
                          </Box>

                          {/* Draggable Indicators */}
                          <Box sx={{ mt: 1, minHeight: 60 }}>
                            {indicators.length === 0 ? (
                              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                Drop indicators here
                              </Typography>
                            ) : (
                              indicators.map((indicator) => (
                                <Chip
                                  key={indicator}
                                  label={indicator}
                                  size="small"
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, indicator)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Toggle indicator selection
                                    setSelectedTechnicals(prev =>
                                      prev.includes(indicator)
                                        ? prev.filter(tech => tech !== indicator)
                                        : [...prev, indicator]
                                    );
                                  }}
                                  sx={{
                                    mr: 0.5,
                                    mb: 0.5,
                                    cursor: 'pointer',
                                    '&:hover': {
                                      backgroundColor: selectedTechnicals.includes(indicator) ? 'primary.dark' : 'action.hover'
                                    }
                                  }}
                                  color={selectedTechnicals.includes(indicator) ? 'primary' : 'default'}
                                />
                              ))
                            )}
                          </Box>

                          {/* Drop Zone Indicator */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              border: '2px dashed transparent',
                              borderRadius: 1,
                              pointerEvents: 'none',
                              transition: 'border-color 0.2s',
                              '&.drag-over': {
                                borderColor: 'primary.main',
                                bgcolor: 'primary.50'
                              }
                            }}
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Active Groups Summary */}
                  {/*<Box sx={{ mt: 2, p: 1, bgcolor: 'info.50', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Active Charts: {Object.entries(activeGroups).filter(([_, active]) => active).length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {Object.entries(activeGroups)
                        .filter(([_, active]) => active)
                        .map(([name]) => name)
                        .join(', ') || 'None'}
                    </Typography>
                  </Box>*/}
                </Card>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Price Chart" />
          <Tab label="Technical Analysis" />
          <Tab label="Fundamentals" />
          <Tab label="Investment Profile" />
          <Tab label="News & Events" />
          <Tab label="Transaction History" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {/* Price Chart */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Technical Chart ({timeframe} days)
            </Typography>
            {stockPrices.length > 0 ? (
              <div ref={chartRef} style={{ width: '100%', height: 'auto' }} />
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={400} gap={2}>
                <Typography variant="h6" color="textSecondary">
                  No price data available for {stock.symbol}
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  This stock may not be actively tracked or may be delisted.
                  <br />
                  Try browsing stocks with rich data like INFY.NS, HDFCBANK.NS, or TATASTEEL.NS
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onBack}
                >
                  Browse Other Stocks
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Swing Trade Inputs Section */}
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Swing Trade Inputs
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Enter custom peak and trough percentages to calculate swing points for the current timeframe
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Peak %"
                  type="number"
                  value={peakPct}
                  onChange={(e) => setPeakPct(e.target.value)}
                  size="small"
                  fullWidth
                  inputProps={{ min: 1, max: 50 }}
                  placeholder="e.g. 10"
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  label="Trough %"
                  type="number"
                  value={troughPct}
                  onChange={(e) => setTroughPct(e.target.value)}
                  size="small"
                  fullWidth
                  inputProps={{ min: 1, max: 50 }}
                  placeholder="e.g. 10"
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  onClick={handleSwingAnalysis}
                  disabled={swingAnalysisLoading || (!peakPct && !troughPct)}
                  startIcon={swingAnalysisLoading ? <CircularProgress size={16} /> : null}
                  fullWidth
                >
                  {swingAnalysisLoading ? 'Calculating...' : 'Mark'}
                </Button>
              </Grid>

              <Grid item xs={12} sm={2}>
                <Button
                  variant="outlined"
                  onClick={clearSwingAnalysis}
                  disabled={!customSwingData}
                  fullWidth
                >
                  Clear
                </Button>
              </Grid>

              {/* Swing Statistics Display */}
              {customSwingData && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Box textAlign="center">
                      <Typography variant="body2" color="textSecondary">
                        Custom swing analysis for {timeframe} days : {customSwingData.statistics[`nSwing_${customSwingData.peak_pct}_${customSwingData.trough_pct}`]} swings = {' '}
                        {customSwingData.swing_points[`Peak_${customSwingData.peak_pct}`]?.length || 0} peaks, {' '}
                        {customSwingData.swing_points[`Trough_${customSwingData.trough_pct}`]?.length || 0} troughs
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Stock Adjustments Section */}
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Stock Adjustments
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Manage stock splits and bonus share allotments to normalize historical prices
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setShowAdjustmentForm(!showAdjustmentForm);
                  if (showAdjustmentForm) {
                    // Reset form when closing
                    setEditingAdjustmentId(null);
                    setAdjustmentDate('');
                    setAdjustmentType('stock_split');
                    setAdjustmentRatio('');
                    setAdjustmentNotes('');
                    setApplyToFile(false);
                  }
                }}
              >
                {showAdjustmentForm ? 'Cancel' : 'Add Adjustment'}
              </Button>
            </Box>

            {/* Adjustments Table */}
            {adjustments.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Ratio</TableCell>
                      <TableCell>Mode</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {adjustments.map((adj) => (
                      <TableRow key={adj.id}>
                        <TableCell>{new Date(adj.adjustment_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Chip
                            label={adj.adjustment_type === 'stock_split' ? 'Stock Split' : 'Bonus Shares'}
                            size="small"
                            color={adj.adjustment_type === 'stock_split' ? 'primary' : 'secondary'}
                          />
                        </TableCell>
                        <TableCell>{adj.ratio}</TableCell>
                        <TableCell>
                          <Chip
                            label={adj.dry_run ? 'Dry-run' : 'Applied to File'}
                            size="small"
                            color={adj.dry_run ? 'warning' : 'success'}
                            variant={adj.dry_run ? 'outlined' : 'filled'}
                          />
                        </TableCell>
                        <TableCell>{adj.notes || '-'}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={() => {
                              setEditingAdjustmentId(adj.id);
                              setAdjustmentDate(adj.adjustment_date);
                              setAdjustmentType(adj.adjustment_type);
                              setAdjustmentRatio(adj.ratio.toString());
                              setAdjustmentNotes(adj.notes || '');
                              setApplyToFile(!adj.dry_run);
                              setShowAdjustmentForm(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={async () => {
                              if (window.confirm('Delete this adjustment? This will revert price normalization.')) {
                                try {
                                  await stockService.deleteStockAdjustment(stock.symbol, adj.id);
                                  toast.success('Adjustment deleted');
                                  loadAdjustments();
                                  loadStockData(); // Refresh chart
                                } catch (error: any) {
                                  toast.error(error.response?.data?.error || 'Failed to delete adjustment');
                                }
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {adjustments.length === 0 && !showAdjustmentForm && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No adjustments configured. Add a stock split or bonus share adjustment to normalize historical prices.
              </Alert>
            )}

            {/* Add/Edit Adjustment Form */}
            {showAdjustmentForm && (
              <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" gutterBottom>
                  {editingAdjustmentId ? 'Edit Adjustment' : 'Add New Adjustment'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Adjustment Date"
                      type="date"
                      value={adjustmentDate}
                      onChange={(e) => setAdjustmentDate(e.target.value)}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={adjustmentType}
                        label="Type"
                        onChange={(e) => setAdjustmentType(e.target.value as 'stock_split' | 'bonus_shares')}
                      >
                        <MenuItem value="stock_split">Stock Split</MenuItem>
                        <MenuItem value="bonus_shares">Bonus Shares</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="Ratio"
                      type="number"
                      value={adjustmentRatio}
                      onChange={(e) => setAdjustmentRatio(e.target.value)}
                      size="small"
                      fullWidth
                      inputProps={{ min: 0.01, step: 0.01 }}
                      placeholder="e.g. 2.0"
                      required
                      helperText="e.g. 2.0 for 1:2 split"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Notes (optional)"
                      value={adjustmentNotes}
                      onChange={(e) => setAdjustmentNotes(e.target.value)}
                      size="small"
                      fullWidth
                      placeholder="e.g. 1:2 stock split"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Tooltip title="Dry-run: Normalize prices in cache only (file unchanged). Apply to File: Write normalized prices to CSV file permanently.">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          checked={applyToFile}
                          onChange={(e) => setApplyToFile(e.target.checked)}
                        />
                        <Typography variant="body2">
                          Apply to File (uncheck for dry-run mode)
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        onClick={async () => {
                          if (!adjustmentDate || !adjustmentRatio) {
                            toast.error('Please fill in required fields');
                            return;
                          }

                          setSavingAdjustment(true);
                          try {
                            const adjustmentData = {
                              adjustment_date: adjustmentDate,
                              adjustment_type: adjustmentType,
                              ratio: parseFloat(adjustmentRatio),
                              dry_run: !applyToFile,
                              notes: adjustmentNotes || undefined,
                            };

                            if (editingAdjustmentId) {
                              await stockService.updateStockAdjustment(stock.symbol, editingAdjustmentId, adjustmentData);
                              toast.success('Adjustment updated successfully');
                            } else {
                              await stockService.addStockAdjustment(stock.symbol, adjustmentData);
                              toast.success('Adjustment added successfully');
                            }

                            // Reset form
                            setShowAdjustmentForm(false);
                            setEditingAdjustmentId(null);
                            setAdjustmentDate('');
                            setAdjustmentType('stock_split');
                            setAdjustmentRatio('');
                            setAdjustmentNotes('');
                            setApplyToFile(false);

                            // Reload adjustments and chart
                            loadAdjustments();
                            loadStockData();
                          } catch (error: any) {
                            toast.error(error.response?.data?.error || 'Failed to save adjustment');
                          } finally {
                            setSavingAdjustment(false);
                          }
                        }}
                        disabled={savingAdjustment || !adjustmentDate || !adjustmentRatio}
                        startIcon={savingAdjustment ? <CircularProgress size={16} /> : null}
                      >
                        {savingAdjustment ? 'Saving...' : (editingAdjustmentId ? 'Update' : 'Save')}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setShowAdjustmentForm(false);
                          setEditingAdjustmentId(null);
                          setAdjustmentDate('');
                          setAdjustmentType('stock_split');
                          setAdjustmentRatio('');
                          setAdjustmentNotes('');
                          setApplyToFile(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Technical Analysis */}
        {analysisLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress />
          </Box>
        ) : technicalAnalysis ? (
          <Grid container spacing={3}>
            {/* Price Levels */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Price Levels
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Current Price</TableCell>
                        <TableCell align="right">{formatCurrency(technicalAnalysis.last_trade_price)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>52W High</TableCell>
                        <TableCell align="right">{formatCurrency(technicalAnalysis.one_year_high)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>52W Low</TableCell>
                        <TableCell align="right">{formatCurrency(technicalAnalysis.one_year_low)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>All Time High</TableCell>
                        <TableCell align="right">{formatCurrency(technicalAnalysis.lifetime_high)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>All Time Low</TableCell>
                        <TableCell align="right">{formatCurrency(technicalAnalysis.lifetime_low)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>

            {/* Returns */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Returns
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>1 Day</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${technicalAnalysis.day_return >= 0 ? '+' : ''}${safeToFixed(technicalAnalysis.day_return, 2)}%`}
                            color={technicalAnalysis.day_return >= 0 ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>1 Week</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${technicalAnalysis.week_return >= 0 ? '+' : ''}${safeToFixed(technicalAnalysis.week_return, 2)}%`}
                            color={technicalAnalysis.week_return >= 0 ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>1 Month</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${technicalAnalysis.month_return >= 0 ? '+' : ''}${safeToFixed(technicalAnalysis.month_return, 2)}%`}
                            color={technicalAnalysis.month_return >= 0 ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>1 Year</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${technicalAnalysis.one_year_return >= 0 ? '+' : ''}${safeToFixed(technicalAnalysis.one_year_return, 2)}%`}
                            color={technicalAnalysis.one_year_return >= 0 ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>

            {/* Golden/Death Crossover Analysis */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Crossover Analysis
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Last Golden Crossover</TableCell>
                        <TableCell align="right">
                          {technicalAnalysis.last_golden_crossover
                            ? new Date(technicalAnalysis.last_golden_crossover).toLocaleDateString()
                            : 'N/A'
                          }
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>GCO Change</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${technicalAnalysis.gco_change >= 0 ? '+' : ''}${safeToFixed(technicalAnalysis.gco_change, 2)}%`}
                            color={technicalAnalysis.gco_change >= 0 ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>GCO Hit Rate</TableCell>
                        <TableCell align="right">{safeToFixed(technicalAnalysis.gco_hit_rate, 1)}%</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>GCO Avg Return</TableCell>
                        <TableCell align="right">{safeToFixed(technicalAnalysis.gco_avg_return, 2)}%</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>GCO Count</TableCell>
                        <TableCell align="right">{safeCount(technicalAnalysis.gco_count)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>

            {/* Swing Analysis */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Swing Analysis
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                    Based on last 3 years of price data
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Swings (10%)</TableCell>
                        <TableCell align="right">{safeCount(technicalAnalysis.n_swing_10)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Avg Days (10%)</TableCell>
                        <TableCell align="right">{safeToFixed(technicalAnalysis.d_swing_10, 1)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Last Swing Value (10%)</TableCell>
                        <TableCell align="right">{formatCurrency(technicalAnalysis.v_swing_10)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Swings (15%)</TableCell>
                        <TableCell align="right">{safeCount(technicalAnalysis.n_swing_15)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Swings (20%)</TableCell>
                        <TableCell align="right">{safeCount(technicalAnalysis.n_swing_20)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>

            {/* Analysis Summary */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Analysis Summary
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Analysis Date: {new Date(technicalAnalysis.analysis_date).toLocaleDateString()}
                  </Typography>
                  <Box mt={2}>
                    <Typography variant="body1">
                      The stock has shown {technicalAnalysis.day_return >= 0 ? 'positive' : 'negative'} movement today
                      ({technicalAnalysis.day_return >= 0 ? '+' : ''}{safeToFixed(technicalAnalysis.day_return, 2)}%).
                      {technicalAnalysis.gco_count > 0 && (
                        <>
                          {' '}Based on {technicalAnalysis.gco_count} golden crossovers, the average return has been {safeToFixed(technicalAnalysis.gco_avg_return, 2)}%
                          with a hit rate of {safeToFixed(technicalAnalysis.gco_hit_rate, 1)}%.
                        </>
                      )}
                      {technicalAnalysis.n_swing_10 > 0 && (
                        <>
                          {' '}The stock has experienced {technicalAnalysis.n_swing_10} significant price swings (10%+)
                          with an average duration of {safeToFixed(technicalAnalysis.d_swing_10, 1)} days.
                        </>
                      )}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {/* Show basic price data if available */}
            {stockPrices.length > 0 && (
              <>
                {/* Basic Price Levels from Price Data */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Price Levels (from Price Data)
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>Current Price</TableCell>
                            <TableCell align="right">{formatCurrency(stockPrices[stockPrices.length - 1]?.close)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>52W High</TableCell>
                            <TableCell align="right">{formatCurrency(Math.max(...stockPrices.slice(-252).map(p => p.high)))}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>52W Low</TableCell>
                            <TableCell align="right">{formatCurrency(Math.min(...stockPrices.slice(-252).map(p => p.low)))}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>All Time High</TableCell>
                            <TableCell align="right">{formatCurrency(Math.max(...stockPrices.map(p => p.high)))}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>All Time Low</TableCell>
                            <TableCell align="right">{formatCurrency(Math.min(...stockPrices.map(p => p.low)))}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Basic Returns Calculation */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Returns (Calculated)
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>1 Day</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${stockPrices.length >= 2 ?
                                  (((stockPrices[stockPrices.length - 1].close - stockPrices[stockPrices.length - 2].close) / stockPrices[stockPrices.length - 2].close) * 100).toFixed(2)
                                  : '0.00'}%`}
                                color={stockPrices.length >= 2 && stockPrices[stockPrices.length - 1].close > stockPrices[stockPrices.length - 2].close ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>1 Week</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${stockPrices.length >= 7 ?
                                  (((stockPrices[stockPrices.length - 1].close - stockPrices[stockPrices.length - 7].close) / stockPrices[stockPrices.length - 7].close) * 100).toFixed(2)
                                  : '0.00'}%`}
                                color={stockPrices.length >= 7 && stockPrices[stockPrices.length - 1].close > stockPrices[stockPrices.length - 7].close ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>1 Month</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${stockPrices.length >= 30 ?
                                  (((stockPrices[stockPrices.length - 1].close - stockPrices[stockPrices.length - 30].close) / stockPrices[stockPrices.length - 30].close) * 100).toFixed(2)
                                  : '0.00'}%`}
                                color={stockPrices.length >= 30 && stockPrices[stockPrices.length - 1].close > stockPrices[stockPrices.length - 30].close ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>1 Year</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${stockPrices.length >= 252 ?
                                  (((stockPrices[stockPrices.length - 1].close - stockPrices[stockPrices.length - 252].close) / stockPrices[stockPrices.length - 252].close) * 100).toFixed(2)
                                  : '0.00'}%`}
                                color={stockPrices.length >= 252 && stockPrices[stockPrices.length - 1].close > stockPrices[stockPrices.length - 252].close ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}

            {/* No Data Available Message */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Complete Technical Analysis
                  </Typography>
                  <Typography color="textSecondary" paragraph>
                    Complete technical analysis data (including crossover analysis, swing analysis, and advanced metrics) is not available for this stock.
                    {stockPrices.length > 0 ? ' Basic calculations are shown above based on price data.' : ' No price data is available either.'}
                  </Typography>
                  <Box display="flex" gap={2}>
                    <Button
                      variant="contained"
                      onClick={runTechnicalAnalysisForStock}
                      disabled={analysisLoading}
                      startIcon={analysisLoading ? <CircularProgress size={20} /> : undefined}
                    >
                      Run Technical Analysis
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={loadTechnicalAnalysis}
                      disabled={analysisLoading}
                    >
                      Retry Loading
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {/* Fundamentals */}
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6">
                Fundamental Analysis
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={scrapingLoading ? <CircularProgress size={16} color="inherit" /> : null}
                onClick={handleInstantScraping}
                disabled={scrapingLoading || !localStorage.getItem('token')}
                sx={{ minWidth: 140 }}
                title={!localStorage.getItem('token') ? 'Please log in to use this feature' : 'Scrape fresh data for this stock'}
              >
                {scrapingLoading ? 'Scraping...' : 'Scrape Data'}
              </Button>
            </Box>

            {/* Scraping Result Status */}
            {scrapingResult && (
              <Alert
                severity={scrapingResult.success ? "success" : "error"}
                sx={{ mb: 2 }}
                onClose={() => setScrapingResult(null)}
              >
                {scrapingResult.success ? (
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      ✅ {scrapingResult.message}
                    </Typography>
                    {scrapingResult.data_summary && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        📊 {scrapingResult.data_summary.significant_values_count} key metrics, {scrapingResult.data_summary.tables_count} tables scraped
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2">
                    ❌ {scrapingResult.error}
                  </Typography>
                )}
              </Alert>
            )}

            {fundamentalData ? (
              <Grid container spacing={3}>
                {/* Key Financial Metrics */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Key Financial Metrics
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        {fundamentalData.significant_values && Object.entries(fundamentalData.significant_values).map(([key, value]: [string, any]) => (
                          <TableRow key={key}>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>
                              {key}
                            </TableCell>
                            <TableCell align="right">
                              {typeof value === 'number' ?
                                (key.toLowerCase().includes('price') || key.toLowerCase().includes('value') ?
                                  formatCurrency(value) :
                                  key.toLowerCase().includes('yield') || key.toLowerCase().includes('return') || key.toLowerCase().includes('ratio') ?
                                    `${value}%` :
                                    formatNumber(value)
                                ) :
                                value
                              }
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Shareholding Pattern */}
                {fundamentalData.shareholding_pattern && fundamentalData.shareholding_pattern.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Shareholding Pattern
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Category</TableCell>
                            <TableCell align="right">Percentage</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fundamentalData.shareholding_pattern.map((item: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell component="th" scope="row">
                                {item.category}
                              </TableCell>
                              <TableCell align="right">
                                {item.percentage}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}

                {/* Quarterly Results Table */}
                {fundamentalData.tables && fundamentalData.tables.quarterly_results && fundamentalData.tables.quarterly_results.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Quarterly Results
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {fundamentalData.tables.quarterly_results[0] && fundamentalData.tables.quarterly_results[0].map((header: string, index: number) => (
                              <TableCell key={index} sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fundamentalData.tables.quarterly_results.slice(1, 11).map((row: string[], rowIndex: number) => (
                            <TableRow key={rowIndex}>
                              {row.map((cell: string, cellIndex: number) => (
                                <TableCell key={cellIndex}>
                                  {cell}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}

                {/* Profit & Loss Table */}
                {fundamentalData.tables && fundamentalData.tables.profit_loss && fundamentalData.tables.profit_loss.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Profit & Loss (Annual)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {fundamentalData.tables.profit_loss[0] && fundamentalData.tables.profit_loss[0].map((header: string, index: number) => (
                              <TableCell key={index} sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fundamentalData.tables.profit_loss.slice(1, 11).map((row: string[], rowIndex: number) => (
                            <TableRow key={rowIndex}>
                              {row.map((cell: string, cellIndex: number) => (
                                <TableCell key={cellIndex}>
                                  {cell}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}

                {/* Balance Sheet Table */}
                {fundamentalData.tables && fundamentalData.tables.balance_sheet && fundamentalData.tables.balance_sheet.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Balance Sheet
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {fundamentalData.tables.balance_sheet[0] && fundamentalData.tables.balance_sheet[0].map((header: string, index: number) => (
                              <TableCell key={index} sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fundamentalData.tables.balance_sheet.slice(1, 11).map((row: string[], rowIndex: number) => (
                            <TableRow key={rowIndex}>
                              {row.map((cell: string, cellIndex: number) => (
                                <TableCell key={cellIndex}>
                                  {cell}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}

                {/* Data Source Info */}
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Data scraped from {fundamentalData.source || 'screener.in'} on{' '}
                    {fundamentalData.scraped_at ? new Date(fundamentalData.scraped_at).toLocaleDateString() : 'Unknown'}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  {fundamentalDataLoaded ?
                    'No fundamental data available for this stock.' :
                    'Loading fundamental data...'
                  }
                </Typography>
                {!fundamentalDataLoaded && (
                  <Button
                    variant="outlined"
                    onClick={loadFundamentalData}
                    sx={{ mt: 2 }}
                  >
                    Load Fundamental Data
                  </Button>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {/* Investment Profile */}
        <Grid container spacing={3}>
          {/* Event Impact History */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6">Event Impact History</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={loadEventHistory}
                    disabled={eventHistoryLoading}
                    startIcon={eventHistoryLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                  >
                    Refresh
                  </Button>
                </Box>
                
                {eventSummary && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Avg Drawdown</Typography>
                        <Typography variant="h6" color={eventSummary.avg_drawdown < 0 ? 'error.main' : 'success.main'}>
                          {eventSummary.avg_drawdown?.toFixed(1)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Max Drawdown</Typography>
                        <Typography variant="h6" color="error.main">
                          {eventSummary.max_drawdown?.toFixed(1)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Avg Recovery</Typography>
                        <Typography variant="h6" color="success.main">
                          {eventSummary.avg_recovery?.toFixed(1)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Avg Resilience</Typography>
                        <Typography variant="h6" color={eventSummary.avg_resilience >= 50 ? 'success.main' : 'warning.main'}>
                          {eventSummary.avg_resilience?.toFixed(0)}/100
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
                
                {eventHistory.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Event</TableCell>
                          <TableCell align="right">Max Drop</TableCell>
                          <TableCell align="right">Days to Bottom</TableCell>
                          <TableCell align="right">Recovery Days</TableCell>
                          <TableCell align="center">Recovered</TableCell>
                          <TableCell align="right">Resilience</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {eventHistory.map((impact: any) => (
                          <TableRow key={impact.id}>
                            <TableCell>{impact.event_name}</TableCell>
                            <TableCell align="right" sx={{ color: impact.max_drawdown_pct < 0 ? 'error.main' : 'success.main' }}>
                              {impact.max_drawdown_pct?.toFixed(1)}%
                            </TableCell>
                            <TableCell align="right">{impact.days_to_bottom}</TableCell>
                            <TableCell align="right">{impact.days_to_recovery ?? '-'}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={impact.recovered_to_baseline ? 'Yes' : 'No'}
                                size="small"
                                color={impact.recovered_to_baseline ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={impact.resilience_score?.toFixed(0)}
                                size="small"
                                sx={{
                                  bgcolor: impact.resilience_score >= 70 ? '#4caf50' : impact.resilience_score >= 50 ? '#ff9800' : '#f44336',
                                  color: 'white'
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="textSecondary">
                    No event impact data available. Run event analysis from the Investment Strategies page.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Value Stock Score */}
          {valueScore && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Value Assessment</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2">Combined Score</Typography>
                      <Chip
                        label={`${valueScore.combined_value_score?.toFixed(0)}/100`}
                        sx={{
                          bgcolor: valueScore.combined_value_score >= 75 ? '#4caf50' : valueScore.combined_value_score >= 55 ? '#8bc34a' : valueScore.combined_value_score >= 35 ? '#ff9800' : '#f44336',
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2">Fundamental Score</Typography>
                      <Typography variant="body2" fontWeight="medium">{valueScore.fundamental_score?.toFixed(0)}/100</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2">Technical Score</Typography>
                      <Typography variant="body2" fontWeight="medium">{valueScore.technical_score?.toFixed(0)}/100</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">Recommendation</Typography>
                      <Chip
                        label={valueScore.recommendation?.replace('_', ' ').toUpperCase()}
                        size="small"
                        color={
                          valueScore.recommendation === 'strong_buy' || valueScore.recommendation === 'buy' ? 'success' :
                          valueScore.recommendation === 'hold' ? 'warning' : 'error'
                        }
                      />
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    P/E: {valueScore.pe_ratio?.toFixed(1) || '-'} | P/B: {valueScore.pb_ratio?.toFixed(2) || '-'} | ROE: {valueScore.roe?.toFixed(1) || '-'}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Active Swing Signals */}
          <Grid item xs={12} md={valueScore ? 6 : 12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Swing Trade Signals</Typography>
                {swingSignals.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Entry</TableCell>
                          <TableCell align="right">Target</TableCell>
                          <TableCell align="right">Stop</TableCell>
                          <TableCell align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {swingSignals.slice(0, 5).map((signal: any) => (
                          <TableRow key={signal.id}>
                            <TableCell>{signal.signal_date}</TableCell>
                            <TableCell>
                              <Chip
                                label={signal.signal_type === 'momentum_breakout' ? 'Momentum' : 'Mean Rev'}
                                size="small"
                                variant="outlined"
                                color={signal.signal_type === 'momentum_breakout' ? 'primary' : 'secondary'}
                              />
                            </TableCell>
                            <TableCell align="right">₹{signal.entry_price?.toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>₹{signal.target_price?.toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ color: 'error.main' }}>₹{signal.stop_loss?.toFixed(2)}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={signal.status?.replace('_', ' ')}
                                size="small"
                                color={
                                  signal.status === 'active' ? 'primary' :
                                  signal.status === 'target_hit' ? 'success' :
                                  signal.status === 'stopped_out' ? 'error' : 'default'
                                }
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="textSecondary">
                    No active swing signals. Run swing analysis from the Investment Strategies page.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        {/* News & Events */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              News & Events
            </Typography>
            <Typography color="textSecondary">
              Latest news and events for {stock.symbol} will be displayed here.
            </Typography>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        {/* Transaction History */}
        <StockTransactionHistory stockSymbol={stock.symbol} />
      </TabPanel>

      {/* Support/Resistance Help Modal */}
      <Dialog
        open={showSRHelpModal}
        onClose={() => setShowSRHelpModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">
            📊 Support & Resistance Levels - How They Work
          </Typography>
          <IconButton onClick={() => setShowSRHelpModal(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
              What are Support and Resistance Levels?
            </Typography>
            <Typography variant="body2" paragraph>
              Support and resistance levels are key price points where a stock has historically shown
              a tendency to reverse direction. <strong>Support</strong> levels are prices where buying
              pressure has historically prevented further decline, while <strong>Resistance</strong> levels
              are prices where selling pressure has historically prevented further advance.
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
              How Are These Levels Calculated?
            </Typography>
            <Typography variant="body2" paragraph>
              Our algorithm uses a <strong>multi-window pivot point analysis</strong> inspired by TradingView's approach:
            </Typography>
            
            <Box component="ol" sx={{ pl: 2, '& li': { mb: 1.5 } }}>
              <li>
                <Typography variant="body2">
                  <strong>Pivot Detection:</strong> We scan the price history using multiple window sizes
                  (5, 10, and 20 days) to identify local highs (potential resistance) and local lows
                  (potential support). A pivot high occurs when a price is the highest within its window,
                  and vice versa for pivot lows.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Price Clustering:</strong> Nearby pivot points (within 1.5% of each other) are
                  grouped together to form a single level. This prevents showing multiple lines at nearly
                  identical prices.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Touch Count:</strong> The number of times price has tested a level. More touches
                  indicate a stronger, more significant level.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Significance Score:</strong> Calculated based on touch count and the window size
                  of the pivots. Larger window pivots are more significant as they represent longer-term
                  price action.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Relevance Filter:</strong> Only levels within 30% of the current price are shown,
                  ensuring the displayed levels are actionable for current trading decisions.
                </Typography>
              </li>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
              Understanding the Display
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: 'rgba(239, 83, 80, 0.1)', borderRadius: 1, border: '1px solid rgba(239, 83, 80, 0.3)' }}>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(239, 83, 80, 1)', fontWeight: 'bold' }}>
                    Resistance Levels (Red Lines)
                  </Typography>
                  <Typography variant="body2">
                    Price levels <strong>above</strong> the current price where selling pressure may emerge.
                    Labeled as R1, R2, R3, etc. (R1 being closest to current price).
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: 'rgba(38, 166, 154, 0.1)', borderRadius: 1, border: '1px solid rgba(38, 166, 154, 0.3)' }}>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(38, 166, 154, 1)', fontWeight: 'bold' }}>
                    Support Levels (Green Lines)
                  </Typography>
                  <Typography variant="body2">
                    Price levels <strong>below</strong> the current price where buying pressure may emerge.
                    Labeled as S1, S2, S3, etc. (S1 being closest to current price).
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
              Line Styles
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell><strong>Solid Line</strong></TableCell>
                  <TableCell>High significance level (significance ≥ 3)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Dashed Line</strong></TableCell>
                  <TableCell>Lower significance level (significance &lt; 3)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Thicker Line</strong></TableCell>
                  <TableCell>Level tested 3 or more times</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Opacity</strong></TableCell>
                  <TableCell>Closer levels are more opaque; further levels fade</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ bgcolor: 'info.50', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
            <Typography variant="subtitle2" color="info.main" gutterBottom>
              💡 Trading Tip
            </Typography>
            <Typography variant="body2">
              Support and resistance levels are more reliable when they have been tested multiple times.
              Look for levels with high touch counts and significance scores. Remember that once a support
              level is broken, it often becomes resistance, and vice versa.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default StockDetails; 